import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package2, 
  Search,
  Filter,
  Edit2,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Info,
  Calendar,
  User,
  Clock,
  Link as LinkIcon,
  AlertTriangle,
  SlidersHorizontal,
  Grid,
  List,
  ArrowUpDown,
  Copy,
  ChevronLeft,
  ChevronRight,
  Shield,
  MapPin,
  FileText,
  Download,
  Upload,
  CheckSquare,
  ImageIcon,
  FileSpreadsheet,
  FileJson,
  Minus,
  Edit3,
  Package,
  Eye,
  EyeOff,
  Lock,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';

// Initialize Supabase clients
const supabaseUrl = import.meta.env.VITE_FILE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_FILE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Initialize User Profiles Supabase client (different database)
const userProfilesUrl = import.meta.env.VITE_SUPABASE_URL;
const userProfilesKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const userProfilesSupabase = createClient(userProfilesUrl, userProfilesKey);

// Add CSS for visible scrollbars
const scrollbarStyles = `
  .visible-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05);
  }
  
  .dark .visible-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
  }

  .visible-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    display: block;
  }
  
  .visible-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
  }
  
  .visible-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
  }
  
  .visible-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  
  .dark .visible-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  .dark .visible-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
  }
  
  .dark .visible-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Türkçe karakterleri normalize etme
const normalizeText = (text) => {
  if (!text) return '';
  return text.toString()
    .toLowerCase()
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ı/g, 'i')
    .replace(/i̇/g, 'i') // büyük İ normalize edildiğinde i̇ olabiliyor
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Aksanları kaldır
};

// Bulanık arama için benzerlik puanı hesaplama
const getSimilarityScore = (source, target) => {
  if (!source || !target) return 0;
  
  // Her iki stringi de normalize et
  const normalizedSource = normalizeText(source);
  const normalizedTarget = normalizeText(target);
  
  // Tam eşleşme - maksimum puan
  if (normalizedSource === normalizedTarget) return 100;
  
  // Kapsama kontrolü (omer, ömer gibi normalizasyon sonrası eşleşmeler)
  if (normalizedSource.includes(normalizedTarget)) return 90;
  if (normalizedTarget.includes(normalizedSource)) return 80;
  
  // Levenshtein mesafesi (benzerlik): kaç değişiklik yaparak eşleşebilirler
  const calculateLevenshteinDistance = (a, b) => {
    const matrix = Array(a.length + 1).fill().map(() => Array(b.length + 1).fill(0));
    
    for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
    
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i-1] === b[j-1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i-1][j] + 1,      // silme
          matrix[i][j-1] + 1,      // ekleme
          matrix[i-1][j-1] + cost  // değiştirme
        );
      }
    }
    
    return matrix[a.length][b.length];
  };
  
  // Levenshtein mesafesini maksimum karakter sayısına oranla
  const maxLength = Math.max(normalizedSource.length, normalizedTarget.length);
  if (maxLength === 0) return 0;
  
  const distance = calculateLevenshteinDistance(normalizedSource, normalizedTarget);
  const similarity = 1 - (distance / maxLength);
  
  // 0-70 arası bir puan döndür (çok farklı stringler için düşük puan)
  return Math.round(similarity * 70);
};

// Arama skorunu ve eşleşen alanları hesaplama
const getSearchScore = (product, searchTerm) => {
  if (!searchTerm) return { score: 0, matches: [] };
  
  const term = normalizeText(searchTerm);
  const matches = [];
  let totalScore = 0;
  
  // Farklı alanlara farklı ağırlıklar verelim
  const fieldWeights = {
    name: 10,       // Ürün adı en önemli
    location: 7,    // Konum ikinci derecede önemli
    description: 5, // Açıklama daha az önemli
    created_by: 3   // Ekleyen kişi en az önemli
  };
  
  // Her alan için benzerlik skorunu hesapla
  const nameScore = getSimilarityScore(product.name, term) * fieldWeights.name;
  if (nameScore > 0) {
    matches.push({ field: 'name', score: nameScore });
    totalScore += nameScore;
  }
  
  if (product.location) {
    const locationScore = getSimilarityScore(product.location, term) * fieldWeights.location;
    if (locationScore > 0) {
      matches.push({ field: 'location', score: locationScore });
      totalScore += locationScore;
    }
  }
  
  if (product.description) {
    const descScore = getSimilarityScore(product.description, term) * fieldWeights.description;
    if (descScore > 0) {
      matches.push({ field: 'description', score: descScore });
      totalScore += descScore;
    }
  }
  
  if (product.created_by) {
    const creatorScore = getSimilarityScore(product.created_by, term) * fieldWeights.created_by;
    if (creatorScore > 0) {
      matches.push({ field: 'created_by', score: creatorScore });
      totalScore += creatorScore;
    }
  }
  
  // Eşleşen kelime sayısına göre de bonus puan ekleyelim
  const wordCount = term.split(/\s+/).filter(Boolean).length;
  if (wordCount > 1) {
    // Çok kelimeli aramalar için tam eşleşme daha değerli
    const fullTextMatches = [
      product.name, 
      product.location, 
      product.description, 
      product.created_by
    ].filter(Boolean).map(field => {
      const normalizedField = normalizeText(field);
      return normalizedField.includes(term) ? 50 : 0; // Tam eşleşme bonusu
    });
    
    const fullTextBonus = Math.max(...fullTextMatches);
    totalScore += fullTextBonus;
  }
  
  // Sonuçları alan adlarına göre düzenle
  matches.sort((a, b) => b.score - a.score);
  
  // Eşleşen alanların adlarını çıkart
  const matchedFields = matches.map(m => m.field);
  
  return { 
    score: totalScore, 
    matches: matchedFields,
    matchDetails: matches // Debugging ve detay gösterimi için
  };
};

// Add Stock Management Modal Component (before StokYonetimi component)
const StockManagementModal = ({ 
  isOpen, 
  onClose, 
  selectedProduct,
  onStockUpdate,
  userProfile 
}) => {
  const [stockAmount, setStockAmount] = useState('');
  const [stockOperation, setStockOperation] = useState('add');
  const [stockError, setStockError] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const inputRef = useRef(null);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setStockAmount('');
      setStockOperation('add');
      setStockError(null);
      setStockLoading(false);
      // Focus input after modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleStockUpdate = async () => {
    if (!selectedProduct || (stockOperation !== 'reset' && !stockAmount)) return;

    try {
      setStockLoading(true);
      setStockError(null);
      const currentStock = selectedProduct.stock;
      let newStock;
      let operationType;

      // Validate stock removal
      if (stockOperation === 'remove') {
        const removeAmount = parseInt(stockAmount);
        
        if (currentStock === 0) {
          setStockError({
            title: 'Stokta Ürün Yok',
            message: 'Bu ürünün stok miktarı 0. Stok çıkışı yapılamaz.',
            type: 'error'
          });
          return;
        }

        if (removeAmount > currentStock) {
          setStockError({
            title: 'Yetersiz Stok',
            message: `Çıkarmak istediğiniz miktar (${removeAmount}) mevcut stok miktarından (${currentStock}) fazla. Lütfen daha az miktar girin.`,
            type: 'error'
          });
          return;
        }

        newStock = currentStock - removeAmount;
        operationType = 'remove';
      } else if (stockOperation === 'add') {
        newStock = currentStock + parseInt(stockAmount);
        operationType = 'add';
      } else if (stockOperation === 'reset') {
        if (currentStock === 0) {
          setStockError({
            title: 'Stok Zaten Sıfır',
            message: 'Bu ürünün stok miktarı zaten 0. Sıfırlama işlemi yapılamaz.',
            type: 'warning'
          });
          return;
        }
        newStock = 0;
        operationType = 'reset';
      }

      await onStockUpdate({
        newStock,
        operationType,
        stockAmount: stockOperation === 'reset' ? currentStock : parseInt(stockAmount)
      });

      onClose();
    } catch (err) {
      setStockError({
        title: 'Hata',
        message: 'Stok güncellenirken bir hata oluştu: ' + err.message,
        type: 'error'
      });
    } finally {
      setStockLoading(false);
    }
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'Belirtilmemiş';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (!isOpen || !selectedProduct) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={false}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">

            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Stok İşlemleri
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>

            
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {selectedProduct.name}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Mevcut Stok: {selectedProduct.stock}
            </p>
          </div>

          {/* Stock Error Message */}
          {stockError && (
            <div className={`mb-4 p-4 rounded-lg ${
              stockError.type === 'error' 
                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
                : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-start">
                {stockError.type === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 mr-3" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mt-0.5 mr-3" />
                )}
                <div>
                  <h5 className={`text-sm font-medium ${
                    stockError.type === 'error'
                      ? 'text-red-800 dark:text-red-300'
                      : 'text-yellow-800 dark:text-yellow-300'
                  }`}>
                    {stockError.title}
                  </h5>
                  <p className={`mt-1 text-sm ${
                    stockError.type === 'error'
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-yellow-700 dark:text-yellow-400'
                  }`}>
                    {stockError.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setStockOperation('add')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  stockOperation === 'add'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Stok Girişi
              </button>
              <button
                onClick={() => setStockOperation('remove')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  stockOperation === 'remove'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Stok Çıkışı
              </button>
              <button
                onClick={() => {
                  setStockOperation('reset');
                  setStockAmount('');
                }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  stockOperation === 'reset'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Stoğu Sıfırla
              </button>
            </div>

            {stockOperation !== 'reset' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {stockOperation === 'add' ? 'Eklenecek Miktar' : 'Çıkarılacak Miktar'}
                </label>
                <input
                  ref={inputRef}
                  type="number"
                  value={stockAmount}
                  onChange={(e) => {
                    setStockAmount(e.target.value);
                    setStockError(null);
                  }}
                  min="1"
                  max={stockOperation === 'remove' && selectedProduct?.stock ? selectedProduct.stock : ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={`Miktar girin${stockOperation === 'remove' ? ` (Maksimum: ${selectedProduct.stock})` : ''}`}
                />
                {stockOperation === 'remove' && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Maksimum çıkarılabilecek miktar: {selectedProduct.stock}
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleStockUpdate}
                disabled={stockLoading || (stockOperation !== 'reset' && !stockAmount)}
                className={`px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2 ${
                  stockLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : stockOperation === 'add'
                    ? 'bg-green-600 hover:bg-green-700'
                    : stockOperation === 'remove'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {stockLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>İşleniyor...</span>
                  </>
                ) : (
                  <>
                    <ArrowUpDown className="w-4 h-4" />
                    <span>
                      {stockOperation === 'add'
                        ? 'Stok Ekle'
                        : stockOperation === 'remove'
                        ? 'Stok Çıkar'
                        : 'Stok Sıfırla'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Stock History Section */}
          <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Stok İşlem Geçmişi
            </h4>
            <div className="space-y-3 max-h-60 overflow-y-auto visible-scrollbar">
              {selectedProduct.stock_history ? (
                selectedProduct.stock_history.map((history, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {history.type === 'add' ? (
                        <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full">
                          <ArrowUpDown className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                      ) : history.type === 'remove' ? (
                        <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
                          <ArrowUpDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </div>
                      ) : (
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-full">
                          <ArrowUpDown className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {history.type === 'add' 
                            ? `+${history.amount} Adet Eklendi`
                            : history.type === 'remove'
                            ? `-${history.amount} Adet Çıkarıldı`
                            : 'Stok Sıfırlandı'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {history.previous_stock} → {history.new_stock} Adet
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {history.updated_by} tarafından
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(history.updated_at)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  Henüz stok işlemi yapılmamış
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const StokYonetimi = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsPerPageMenu, setShowItemsPerPageMenu] = useState(false);
  const [filterConfig, setFilterConfig] = useState({
    showLowStock: false,
    showOutOfStock: false,
    showWithImage: false,
    showWithoutImage: false,
    locationPrefix: '',
    dateRange: {
      from: '',
      to: ''
    },
    createdBy: '',
    stockSort: '', // 'asc' or 'desc'
    dateSort: '', // 'newest' or 'oldest'
    stockRange: {
      min: '',
      max: ''
    }
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [productToDelete, setProductToDelete] = useState(null);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [uniqueCreators, setUniqueCreators] = useState([]);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState(null);
  const [showUndoNotification, setShowUndoNotification] = useState(false);
  const [lastOperation, setLastOperation] = useState(null);
  const [undoCountdown, setUndoCountdown] = useState(10);
  const countdownRef = useRef(null);
  const [stockError, setStockError] = useState(null);

  // Add userProfile state
  const [userProfile, setUserProfile] = useState(null);
  // Add permissions state
  const [permissions, setPermissions] = useState({
    stok_add_permission: false,
    stok_edit_permission: false,
    stok_movement_permission: false,
    stok_delete_permission: false
  });

  // In the StokYonetimi component, add these state variables with other states
  const [deletionProgress, setDeletionProgress] = useState(0);
  const [showDeletionProgress, setShowDeletionProgress] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [totalToDelete, setTotalToDelete] = useState(0);

  // Add these new state variables to StokYonetimi component, near other state declarations:
  const [recentSearches, setRecentSearches] = useState([]);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  
  // Then, modify the onChange handler for search input by adding this effect to save recent searches:

  // Update the search term and save to recent searches
  useEffect(() => {
    if (searchTerm.trim()) {
      // Start searching animation
      setIsSearching(true);
      
      // Simulate search delay for loading animation
      const timer = setTimeout(() => {
        setIsSearching(false);
        
        // Add to recent searches if not already in list
        if (!recentSearches.includes(searchTerm.trim()) && searchTerm.trim().length > 2) {
          setRecentSearches(prev => {
            const newSearches = [searchTerm.trim(), ...prev.filter(s => s !== searchTerm.trim())].slice(0, 5);
            // Save to localStorage
            localStorage.setItem('stockManagementRecentSearches', JSON.stringify(newSearches));
            return newSearches;
          });
        }
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setIsSearching(false);
    }
  }, [searchTerm]);
  
  // Function to remove a single search term from recent searches
  const removeSearchTerm = (termToRemove) => {
    setRecentSearches(prev => {
      const newSearches = prev.filter(term => term !== termToRemove);
      localStorage.setItem('stockManagementRecentSearches', JSON.stringify(newSearches));
      if (newSearches.length === 0) {
        setShowRecentSearches(false);
      }
      return newSearches;
    });
  };
  
  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('stockManagementRecentSearches');
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  }, []);
  
  // Close recent searches dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowRecentSearches(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch user profile when component mounts - Update this function
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // First get the user from the auth client
        const { data: { user }, error: authError } = await userProfilesSupabase.auth.getUser();
        
        if (authError) {
          console.error('Auth error:', authError);
          return;
        }

        if (user) {
          // Then fetch the user profile from user_profiles table with permissions
          const { data, error: profileError } = await userProfilesSupabase
            .from('user_profiles')
            .select('*, first_name, last_name, role, stok_add_permission, stok_edit_permission, stok_movement_permission, stok_delete_permission')
            .eq('id', user.id)
            .single();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            return;
          }

          if (data) {
            setUserProfile(data);
            // Set permissions based on user profile
            setPermissions({
              stok_add_permission: Boolean(data.stok_add_permission),
              stok_edit_permission: Boolean(data.stok_edit_permission),
              stok_movement_permission: Boolean(data.stok_movement_permission),
              stok_delete_permission: Boolean(data.stok_delete_permission)
            });
          } else {
            console.warn('No user profile found for user:', user.id);
          }
        } else {
          console.warn('No authenticated user found');
        }
      } catch (error) {
        console.error('Error in fetchUserProfile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  // Fetch products from Supabase
  useEffect(() => {
    fetchProducts();
  }, []);

  // Extract unique location prefixes and creators when products are loaded
  useEffect(() => {
    if (products.length > 0) {
      // Extract unique location prefixes (first letter)
      const locationPrefixes = [...new Set(products.map(product => 
        product.location ? product.location.charAt(0) : null
      ))].filter(Boolean).sort();
      setUniqueLocations(locationPrefixes);

      // Extract unique creators
      const creators = [...new Set(products.map(product => 
        product.created_by
      ))].filter(Boolean).sort();
      setUniqueCreators(creators);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setProducts(data);
    } catch (err) {
      setError('Ürünler yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete product
  const handleDelete = async (productId, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!canDeleteStock()) {
      toast.error('Ürün silme yetkiniz bulunmamaktadır!', {
        duration: 3000,
        position: 'top-center',
        icon: '🔒',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }

    setProductToDelete(productId);
    setDeleteType('single');
    setShowDeleteModal(true);
  };

  // Handle product selection
  const handleSelectProduct = (productId, event) => {
    // If event is provided and it's not from the checkbox, prevent default behavior
    if (event && event.target.type !== 'checkbox') {
      event.preventDefault();
      event.stopPropagation();
    }

    // Close product details popup if it's open
    if (selectedProduct) {
      setSelectedProduct(null);
    }

    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (!canDeleteStock()) {
      toast.error('Ürün silme yetkiniz bulunmamaktadır!', {
        duration: 3000,
        position: 'top-center',
        icon: '🔒',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }
    
    if (selectedProducts.length === 0) {
      setError('Lütfen silinecek ürünleri seçin');
      return;
    }
    
    setTotalToDelete(selectedProducts.length);
    setDeletedCount(0);
    setDeletionProgress(0);
    setShowDeletionProgress(false);
    setDeleteType('bulk');
    setShowDeleteModal(true);
  };

  // New function to handle image deletion from bucket
  const deleteImageFromBucket = async (imageUrl) => {
    if (!imageUrl) return;
    
    try {
      // Extract file path from the URL - handle different URL patterns
      let filePath = '';
      
      if (imageUrl.includes('storage/v1/object/public/product-images/')) {
        // Standard Supabase URL format
        const regex = /storage\/v1\/object\/public\/product-images\/(.+)/;
        const match = imageUrl.match(regex);
        if (match && match[1]) {
          filePath = match[1];
        }
      } else if (imageUrl.includes('/product-images/')) {
        // Alternative format with product-images folder
        const parts = imageUrl.split('/product-images/');
        if (parts.length > 1) {
          filePath = parts[1];
        }
      } else if (imageUrl.includes('photos/')) {
        // Photos folder format (our upload format)
        const parts = imageUrl.split('photos/');
        if (parts.length > 1) {
          filePath = `photos/${parts[1]}`;
        }
      } else {
        // Fallback: try to extract filename and assume it's in photos folder
        const urlParts = imageUrl.split('/');
        const fileName = urlParts[urlParts.length - 1];
        if (fileName && fileName.includes('.')) {
          filePath = `photos/${fileName}`;
        }
      }
      
      // Log the extraction for debugging
      console.log('Attempting to delete image:', filePath, 'from URL:', imageUrl);
      
      if (!filePath) {
        console.error('Could not extract file path from URL:', imageUrl);
        return false;
      }
      
      const { error } = await supabase.storage
        .from('product-images')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting image from bucket:', error);
        return false;
      } else {
        console.log('Successfully deleted image:', filePath);
        return true;
      }
    } catch (err) {
      console.error('Error in deleteImageFromBucket:', err);
      return false;
    }
  };

  // Create a memoized DeleteProgressBar component to prevent re-renders of the whole modal
  const DeleteProgressBar = memo(({ progress, count, total }) => {
    const isCompleted = progress === 100;
    
    return (
      <div className={`mb-6 border rounded-lg p-4 transition-all duration-500 ${
        isCompleted 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            {isCompleted ? (
              <div className="w-5 h-5 bg-green-600 dark:bg-green-500 rounded-full flex items-center justify-center mr-2">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            )}
            <h3 className={`text-sm font-medium transition-colors duration-300 ${
              isCompleted 
                ? 'text-green-700 dark:text-green-300' 
                : 'text-red-700 dark:text-red-300'
            }`}>
              {isCompleted 
                ? `${count === 1 ? 'Ürün' : 'Ürünler'} Başarıyla Silindi! ✨`
                : `Ürünler Siliniyor (${count}/${total})`
              }
            </h3>
          </div>
        </div>
        
        <div className={`w-full rounded-full h-2.5 mb-1 transition-colors duration-300 ${
          isCompleted 
            ? 'bg-green-200 dark:bg-green-800/50' 
            : 'bg-red-200 dark:bg-red-800/50'
        }`}>
          <div 
            className={`h-2.5 rounded-full transition-all duration-500 ${
              isCompleted 
                ? 'bg-green-600 dark:bg-green-500' 
                : 'bg-red-600 dark:bg-red-500'
            }`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <p className={`text-xs text-right transition-colors duration-300 ${
          isCompleted 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-red-600 dark:text-red-400'
        }`}>
          {isCompleted ? 'Tamamlandı! 🎉' : `${progress}%`}
        </p>
      </div>
    );
  });
  
  // Optimize the confirmDelete function with useCallback to ensure stability
  const confirmDelete = useCallback(async () => {
    try {
      if (deleteType === 'single' && productToDelete) {
        // Single product deletion
        // Get product details before deletion
        const productToBeDeleted = products.find(p => p.id === productToDelete);
        
        // Show deletion progress
        setShowDeletionProgress(true);
        
        // Delete the product (database trigger will handle deleted_products insertion)
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', productToDelete);

        if (error) throw error;
        
        // Delete the image if exists
        if (productToBeDeleted?.image_url) {
          await deleteImageFromBucket(productToBeDeleted.image_url);
        }
        
        // Update local state
        setProducts(products.filter(p => p.id !== productToDelete));
        setSelectedProducts(selectedProducts.filter(id => id !== productToDelete));
        
        // Show success state
        setDeletionProgress(100);
        setDeletedCount(1);
        setTotalToDelete(1);
        
        // Close modal after showing success
        setTimeout(() => {
          setShowDeletionProgress(false);
          setShowDeleteModal(false);
          setDeleteType(null);
          setProductToDelete(null);
        }, 1500);
        
      } else if (deleteType === 'bulk') {
        // Get products to be deleted
        const productsToBeDeleted = products.filter(p => selectedProducts.includes(p.id));
        const totalCount = productsToBeDeleted.length;
        
        // Start showing progress bar
        setShowDeletionProgress(true);
        setDeletionProgress(0);
        setDeletedCount(0);
        
        const updaterName = userProfile ? 
          `${userProfile.first_name} ${userProfile.last_name}` : 
          'Belirtilmemiş Kullanıcı';
          
        // Create a copy of all products that will be updated at the end
        const productsAfterDeletion = products.filter(p => !selectedProducts.includes(p.id));
        
        // Prevent modal closing during deletion
        const closeModalAfterDeletion = () => {
          // Keep progress bar visible for a moment
          setTimeout(() => {
            setShowDeletionProgress(false);
            setShowDeleteModal(false);
            setDeleteType(null);
            setProductToDelete(null);
          }, 1000);
        };
        
        try {
          // Process deletions in batches for better performance
          const batchSize = 5; // Adjust based on performance
          
          for (let i = 0; i < productsToBeDeleted.length; i += batchSize) {
            const batch = productsToBeDeleted.slice(i, i + batchSize);
            const batchIds = batch.map(p => p.id);
            
            // Delete products in batch (database trigger will handle deleted_products insertion)
            const { error: deleteError } = await supabase
              .from('products')
              .delete()
              .in('id', batchIds);
              
            if (deleteError) console.error('Error deleting products:', deleteError);
            
            // Delete images if needed - Use Promise.all instead of forEach
            const imagePromises = [];
            for (const product of batch) {
              if (product.image_url) {
                imagePromises.push(deleteImageFromBucket(product.image_url));
              }
            }
            
            // Wait for all image deletions to complete
            await Promise.all(imagePromises);
            
            // Update progress after batch completes
            const newCount = Math.min(i + batchSize, totalCount);
            setDeletedCount(newCount);
            setDeletionProgress(Math.floor((newCount / totalCount) * 100));
            
            // Small delay between batches
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Update state after all deletions complete
          setProducts(productsAfterDeletion);
          setSelectedProducts([]);
          closeModalAfterDeletion();
          
        } catch (error) {
          console.error('Error in bulk deletion:', error);
          setError('Ürünler silinirken bir hata oluştu: ' + error.message);
          closeModalAfterDeletion();
        }
      }
    } catch (err) {
      setError('Ürünler silinirken bir hata oluştu: ' + err.message);
      setShowDeletionProgress(false);
      setShowDeleteModal(false);
      setDeleteType(null);
      setProductToDelete(null);
    }
  }, [deleteType, productToDelete, products, selectedProducts, userProfile]);

  // Add Delete Confirmation Modal with optimization
  const DeleteConfirmationModal = memo(() => {
    if (!showDeleteModal) return null;

    // Memoize these values to prevent unnecessary recalculations
    const modalContent = useMemo(() => {
      const title = deleteType === 'single' ? 'Ürünü Sil' : 'Seçili Ürünleri Sil';
      const message = deleteType === 'single' 
        ? 'Bu ürünü silmek istediğinizden emin misiniz?'
        : `${selectedProducts.length} ürünü silmek istediğinizden emin misiniz?`;
        
      return { title, message };
    }, [deleteType, selectedProducts.length]);

    // Memoize handlers to prevent recreating functions on each render
    const handleClose = useCallback(() => setShowDeleteModal(false), []);
    
    return (
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
        >
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {modalContent.title}
                </h3>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {modalContent.message}
              </p>

              {/* Use the memoized progress bar component */}
              {showDeletionProgress && (
                <DeleteProgressBar 
                  progress={deletionProgress} 
                  count={deletedCount} 
                  total={totalToDelete} 
                />
              )}

              {/* Hide buttons when deletion is complete */}
              {!(showDeletionProgress && deletionProgress === 100) && (
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    disabled={showDeletionProgress}
                  >
                    İptal
                  </button>
                  <button
                    onClick={confirmDelete}
                    className={`px-4 py-2 ${showDeletionProgress ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white rounded-lg transition-colors flex items-center gap-2`}
                    disabled={showDeletionProgress}
                  >
                    {showDeletionProgress ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Siliniyor...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        <span>Sil</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  });

  // Sort products
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilterConfig(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Handle date range filter change
  const handleDateRangeChange = (rangeType, value) => {
    setFilterConfig(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [rangeType]: value
      }
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilterConfig({
      showLowStock: false,
      showOutOfStock: false,
      showWithImage: false,
      showWithoutImage: false,
      locationPrefix: '',
      dateRange: {
        from: '',
        to: ''
      },
      createdBy: '',
      stockSort: '',
      dateSort: '',
      stockRange: {
        min: '',
        max: ''
      }
    });
    setSearchTerm('');
  };

  // Check if any filter is active
  const isFilterActive = () => {
    return (
      filterConfig.showLowStock ||
      filterConfig.showOutOfStock ||
      filterConfig.showWithImage ||
      filterConfig.showWithoutImage ||
      filterConfig.locationPrefix !== '' ||
      filterConfig.dateRange.from !== '' ||
      filterConfig.dateRange.to !== '' ||
      filterConfig.createdBy !== '' ||
      filterConfig.stockSort !== '' ||
      filterConfig.dateSort !== '' ||
      filterConfig.stockRange.min !== '' ||
      filterConfig.stockRange.max !== ''
    );
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filterConfig.showLowStock) count++;
    if (filterConfig.showOutOfStock) count++;
    if (filterConfig.showWithImage) count++;
    if (filterConfig.showWithoutImage) count++;
    if (filterConfig.locationPrefix !== '') count++;
    if (filterConfig.dateRange.from !== '' || filterConfig.dateRange.to !== '') count++;
    if (filterConfig.createdBy !== '') count++;
    if (filterConfig.stockSort !== '') count++;
    if (filterConfig.dateSort !== '') count++;
    if (filterConfig.stockRange.min !== '') count++;
    if (filterConfig.stockRange.max !== '') count++;
    return count;
  };

  // Get filtered and sorted products
  const getFilteredAndSortedProducts = () => {
    let filtered = [...products];

    // Apply search filter
    if (searchTerm) {
      // Minimum skor eşiği - bu skorun altında eşleşme gösterme
      const MIN_SCORE_THRESHOLD = 10;
      
      // Her ürün için arama skoru hesapla
      filtered = filtered.map(product => {
        const searchResults = getSearchScore(product, searchTerm);
        
        // Sadece minimum skoru geçen sonuçları dahil et
        if (searchResults.score >= MIN_SCORE_THRESHOLD) {
          return { 
            ...product, 
            searchMatches: searchResults.matches,
            searchScore: searchResults.score,
            matchDetails: searchResults.matchDetails
          };
        }
        return null;
      }).filter(Boolean); // null değerleri filtrele
      
      // Sonuçları alakalılık puanına göre sırala
      filtered.sort((a, b) => b.searchScore - a.searchScore);
      
      // Sadece ilk 5 ürün için görsel işaretler ekle
      filtered = filtered.map((product, index) => ({
        ...product,
        searchVisualMatch: index < 4 // Sadece ilk 8 ürüne görsel etiket ekle
      }));
    }

    // Apply stock filters
    if (filterConfig.showLowStock) {
      filtered = filtered.filter(product => product.stock > 0 && product.stock < 10);
    }
    if (filterConfig.showOutOfStock) {
      filtered = filtered.filter(product => product.stock === 0);
    }

    // Apply image filters
    if (filterConfig.showWithImage) {
      filtered = filtered.filter(product => product.image_url);
    }
    if (filterConfig.showWithoutImage) {
      filtered = filtered.filter(product => !product.image_url);
    }

    // Apply location prefix filter
    if (filterConfig.locationPrefix) {
      filtered = filtered.filter(product => 
        product.location && product.location.startsWith(filterConfig.locationPrefix)
      );
    }

    // Apply date range filter
    if (filterConfig.dateRange.from) {
      const fromDate = new Date(filterConfig.dateRange.from);
      filtered = filtered.filter(product => 
        product.created_at && new Date(product.created_at) >= fromDate
      );
    }
    if (filterConfig.dateRange.to) {
      const toDate = new Date(filterConfig.dateRange.to);
      toDate.setHours(23, 59, 59, 999); // End of the day
      filtered = filtered.filter(product => 
        product.created_at && new Date(product.created_at) <= toDate
      );
    }

    // Apply creator filter
    if (filterConfig.createdBy) {
      filtered = filtered.filter(product => 
        product.created_by === filterConfig.createdBy
      );
    }

    // Apply stock range filter
    if (filterConfig.stockRange.min !== '') {
      filtered = filtered.filter(product => product.stock >= parseInt(filterConfig.stockRange.min));
    }
    if (filterConfig.stockRange.max !== '') {
      filtered = filtered.filter(product => product.stock <= parseInt(filterConfig.stockRange.max));
    }

    // Arama yapmıyorsak normal sıralamayı uygula
    if (!searchTerm) {
      // Stock amount sorting
      if (filterConfig.stockSort === 'asc') {
        filtered.sort((a, b) => a.stock - b.stock);
      }
      if (filterConfig.stockSort === 'desc') {
        filtered.sort((a, b) => b.stock - a.stock);
      }

      // Date sorting
      if (filterConfig.dateSort === 'newest') {
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }
      if (filterConfig.dateSort === 'oldest') {
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      }

      // Default sorting (by name)
      if (sortConfig.key === 'name') {
        filtered.sort((a, b) => sortConfig.direction === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name));
      }
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredAndSortedProducts();

  // Add pagination logic
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Enhanced Pagination Component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const renderPageNumbers = () => {
      const visiblePages = [];
      
      // Always show first page
      visiblePages.push(1);
      
      // Calculate range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Add ellipsis after first page if needed
      if (start > 2) {
        visiblePages.push('...');
      }
      
      // Add pages around current page
      for (let i = start; i <= end; i++) {
        visiblePages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        visiblePages.push('...');
      }
      
      // Always show last page if more than one page
      if (totalPages > 1) {
        visiblePages.push(totalPages);
      }
      
      return visiblePages.map((page, index) => {
        if (page === '...') {
          return (
            <span
              key={`ellipsis-${index}`}
              className="w-10 text-center inline-flex items-center justify-center"
            >
              ⋯
            </span>
          );
        }
        
        return (
          <button
            key={page}
            onClick={() => paginate(page)}
            className={`w-10 h-10 inline-flex items-center justify-center rounded-full text-sm font-medium transition-all ${
              currentPage === page
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 ring-2 ring-green-600 dark:ring-green-400'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {page}
          </button>
        );
      });
    };

    return (
      <div className="px-4 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-b-xl">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Items per page selector */}
          <div className="relative items-per-page-selector">
            <button
              onClick={() => setShowItemsPerPageMenu(!showItemsPerPageMenu)}
              className="h-10 px-4 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 flex items-center gap-2 transition-colors"
            >
              <span className="text-gray-600 dark:text-gray-300">{itemsPerPage}</span>
              <span className="text-gray-400 dark:text-gray-500">ürün/sayfa</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showItemsPerPageMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown menu */}
            {showItemsPerPageMenu && (
              <div 
                className="absolute bottom-full left-0 mb-2 py-2 w-40 rounded-2xl shadow-lg bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700"
                style={{ zIndex: 1000 }}
              >
                {itemsPerPageOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleItemsPerPageChange(option)}
                    className={`w-full px-4 py-2 text-sm text-left transition-colors ${
                      itemsPerPage === option
                        ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    } flex items-center justify-between`}
                  >
                    <span>{option} ürün</span>
                    {itemsPerPage === option && (
                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            {/* First Page Button */}
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className={`w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors ${
                currentPage === 1
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title="İlk Sayfa"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>

            {/* Previous Page Button */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors ${
                currentPage === 1
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title="Önceki Sayfa"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center gap-1">
              {renderPageNumbers()}
            </div>

            {/* Next Page Button */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors ${
                currentPage === totalPages
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title="Sonraki Sayfa"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors ${
                currentPage === totalPages
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title="Son Sayfa"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Page info */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <span className="hidden sm:inline">Toplam </span>
            {filteredProducts.length} üründen {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredProducts.length)}
          </div>
        </div>
      </div>
    );
  };

  // Add click outside handler for items per page menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showItemsPerPageMenu && !event.target.closest('.items-per-page-selector')) {
        setShowItemsPerPageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showItemsPerPageMenu]);

  // Filter Panel Component
  const FilterPanel = () => {
    return (
      <AnimatePresence>
        {showFilterPanel && (
          <motion.div
          initial={false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 mb-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white">Gelişmiş Filtreler</h3>
              <button 
                onClick={() => setShowFilterPanel(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Stock Status Filters */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Stok Durumu</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterConfig.showLowStock}
                      onChange={(e) => handleFilterChange('showLowStock', e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Düşük Stok (10'dan az)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterConfig.showOutOfStock}
                      onChange={(e) => handleFilterChange('showOutOfStock', e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Stokta Yok (0)</span>
                  </label>
                </div>
              </div>
              
              {/* Image Filters */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Görsel Durumu</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterConfig.showWithImage}
                      onChange={(e) => handleFilterChange('showWithImage', e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Görseli Olanlar</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterConfig.showWithoutImage}
                      onChange={(e) => handleFilterChange('showWithoutImage', e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Görseli Olmayanlar</span>
                  </label>
                </div>
              </div>
              
              {/* Location Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Konum</h4>
                <select
                  value={filterConfig.locationPrefix}
                  onChange={(e) => handleFilterChange('locationPrefix', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Tüm Konumlar</option>
                  {uniqueLocations.map(prefix => (
                    <option key={prefix} value={prefix}>
                      {prefix} Sütunu
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Date Range Filters */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Eklenme Tarihi</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Başlangıç</label>
                    <input
                      type="date"
                      value={filterConfig.dateRange.from}
                      onChange={(e) => handleDateRangeChange('from', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Bitiş</label>
                    <input
                      type="date"
                      value={filterConfig.dateRange.to}
                      onChange={(e) => handleDateRangeChange('to', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Creator Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Ekleyen</h4>
                <select
                  value={filterConfig.createdBy}
                  onChange={(e) => handleFilterChange('createdBy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Tüm Kullanıcılar</option>
                  {uniqueCreators.map(creator => (
                    <option key={creator} value={creator}>
                      {creator}
                    </option>
                  ))}
                </select>
              </div>

              {/* Stock Range Filter */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Stok Aralığı</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    placeholder="Min"
                    value={filterConfig.stockRange.min}
                    onChange={(e) => handleFilterChange('stockRange', { ...filterConfig.stockRange, min: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <span className="text-gray-500 dark:text-gray-400">-</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="Max"
                    value={filterConfig.stockRange.max}
                    onChange={(e) => handleFilterChange('stockRange', { ...filterConfig.stockRange, max: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Stock Sort */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Stok Miktarına Göre Sırala</h4>
                <select
                  value={filterConfig.stockSort}
                  onChange={(e) => handleFilterChange('stockSort', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Varsayılan</option>
                  <option value="asc">Azdan Çoğa</option>
                  <option value="desc">Çoktan Aza</option>
                </select>
              </div>

              {/* Date Sort */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Tarihe Göre Sırala</h4>
                <select
                  value={filterConfig.dateSort}
                  onChange={(e) => handleFilterChange('dateSort', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Varsayılan</option>
                  <option value="newest">En Yeni</option>
                  <option value="oldest">En Eski</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mr-3"
              >
                Filtreleri Temizle
              </button>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Filtreleri Uygula
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // Update the ProductModal to include next/previous navigation buttons
  const ProductModal = ({ product, onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
    if (!product) return null;

    // Format date function
    const formatDate = (dateString) => {
      if (!dateString) return 'Belirtilmemiş';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    // Copy to clipboard function
    const copyToClipboard = (text) => {
      // Create a temporary textarea element
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';  // Prevent scrolling to bottom
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        // Execute copy command
        document.execCommand('copy');
        
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
        notification.textContent = 'Metin panoya kopyalandı!';
        document.body.appendChild(notification);
        
        // Remove notification after 2 seconds
        setTimeout(() => {
          notification.remove();
        }, 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      } finally {
        // Clean up
        document.body.removeChild(textarea);
      }
    };

    return (
      <AnimatePresence>
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={false}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-700 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors z-50"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>

            {/* Image Section */}
            <div className="relative h-[300px] flex-shrink-0 bg-gray-100 dark:bg-gray-700">
              {/* Previous button - left side of image */}
              {hasPrevious && (
                <button
                  onClick={onPrevious}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 shadow-md z-10 transition-all"
                  title="Önceki ürün"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700 dark:text-gray-200" />
                </button>
              )}
              
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package2 className="w-16 h-16 text-gray-400" />
                </div>
              )}
              
              {/* Next button - right side of image */}
              {hasNext && (
                <button
                  onClick={onNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 shadow-md z-10 transition-all"
                  title="Sonraki ürün"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700 dark:text-gray-200" />
                </button>
              )}
            </div>

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto" style={{maxHeight: "calc(90vh - 300px - 64px)"}}>
              <div className="p-6">
                {/* Product Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {product.name}
                    </h2>
                    <button
                      onClick={() => copyToClipboard(product.name)}
                      className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Ürün adını kopyala"
                    >
                      <Copy className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {product.location}
                      </span>
                      <button
                        onClick={() => copyToClipboard(product.location)}
                        className="ml-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Konum bilgisini kopyala"
                      >
                        <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                    <div className="flex items-center">
                      {product.stock === 0 ? (
                        <XCircle className="w-5 h-5 text-red-500 mr-2" />
                      ) : product.stock < 10 ? (
                        <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" />
                      )}
                      <span className={`text-sm font-medium ${
                        product.stock === 0
                          ? 'text-red-600 dark:text-red-400'
                          : product.stock < 10
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {product.stock} Adet Mevcut
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {product.description && (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Ürün Açıklaması</h3>
                      <button
                        onClick={() => copyToClipboard(product.description)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Açıklamayı kopyala"
                      >
                        <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
                      {product.description}
                    </p>
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Location Details */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Konum Bilgileri</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="text-gray-600 dark:text-gray-300 font-medium">Dolap Konumu:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{product.location}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-600 dark:text-gray-300 font-medium">Stok Durumu:</span>
                        <span className={`ml-2 ${
                          product.stock === 0
                            ? 'text-red-600 dark:text-red-400'
                            : product.stock < 10
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-green-600 dark:text-green-400'
                        }`}>
                          {product.stock} Adet Mevcut
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Purchase Link */}
                  {product.purchase_link && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Satın Alma Bilgileri</h3>
                      <a
                        href={product.purchase_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        <LinkIcon className="w-4 h-4 mr-2" />
                        <span className="truncate">{product.purchase_link}</span>
                      </a>
                    </div>
                  )}
                </div>

                {/* Metadata Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Sistem Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center text-sm">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Oluşturulma:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {formatDate(product.created_at)}
                          </span>
                        </div>
                      </div>
                      {product.created_by && (
                        <div className="flex items-center text-sm">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Oluşturan:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {product.created_by}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center text-sm">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Son Güncelleme:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {formatDate(product.updated_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center text-sm">
                        <Package2 className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Ürün ID:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {product.id}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Extra space for better scrolling */}
                <div className="h-4"></div>
              </div>
            </div>
            
            {/* Fixed Pagination Zone - This is always at the bottom */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4">
              <div className="flex items-center justify-between">
                {/* Pagination Counter */}
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-900 dark:text-white">{(product.currentIndex !== undefined ? product.currentIndex + 1 : 1)}</span>
                  <span> / </span>
                  <span>{filteredProducts.length}</span>
                  <span className="ml-2">ürün</span>
                </div>
                
                {/* Actions and Navigation Buttons */}
                <div className="flex gap-2">
                  {/* Stock Operations Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStockProduct(product);
                      setShowStockModal(true);
                      setSelectedProduct(null);
                    }}
                    className="px-3 py-1.5 rounded-md flex items-center gap-1 text-sm bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-800/50 transition-colors"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    <span>Stok İşlemleri</span>
                  </button>
                  
                  {/* Edit Product Button */}
                  <Link
                    to={`/stokflow/product/edit/${product.id}`}
                    className="px-3 py-1.5 rounded-md flex items-center gap-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800/50 transition-colors mr-2"
                    onClick={e => {
                      e.stopPropagation();
                      if (!handleEditProduct(product.id, e)) {
                        e.preventDefault();  // izin yoksa yönlendirmeyi iptal et
                      }
                    }}
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>Düzenle</span>
                  </Link>
                  
                  {/* Previous Button */}
                  <button
                    onClick={onPrevious}
                    disabled={!hasPrevious}
                    className={`px-3 py-1.5 rounded-md flex items-center gap-1 text-sm ${
                      hasPrevious 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600' 
                        : 'bg-gray-50 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                    }`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Önceki</span>
                  </button>
                  
                  {/* Next Button */}
                  <button
                    onClick={onNext}
                    disabled={!hasNext}
                    className={`px-3 py-1.5 rounded-md flex items-center gap-1 text-sm ${
                      hasNext 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600' 
                        : 'bg-gray-50 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                    }`}
                  >
                    <span>Sonraki</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Add useEffect for countdown
  useEffect(() => {
    if (showUndoNotification) {
      countdownRef.current = setInterval(() => {
        setUndoCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            setShowUndoNotification(false);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [showUndoNotification]);

  // Add undo operation function
  const handleUndoOperation = async () => {
    if (!lastOperation) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('products')
        .update({
          stock: lastOperation.previous_stock,
          updated_at: new Date().toISOString(),
          updated_by: lastOperation.updated_by,
          stock_history: lastOperation.previous_history
        })
        .eq('id', lastOperation.productId);

      if (error) throw error;

      // Update local state
      setProducts(products.map(p => 
        p.id === lastOperation.productId 
          ? { 
              ...p, 
              stock: lastOperation.previous_stock,
              updated_at: new Date().toISOString(),
              updated_by: lastOperation.updated_by,
              stock_history: lastOperation.previous_history
            }
          : p
      ));

      setShowUndoNotification(false);
      setLastOperation(null);
      setUndoCountdown(10);
    } catch (err) {
      setError('İşlem geri alınırken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update StokYonetimi component to use the new modal
  const handleStockUpdate = async ({ newStock, operationType, stockAmount }) => {
    try {
      // Use userProfile for updater name
      let updaterName = 'Belirtilmemiş Kullanıcı';
      if (userProfile) {
        updaterName = `${userProfile.first_name} ${userProfile.last_name}`;
      }

      // Get current stock history
      const { data: currentProduct } = await supabase
        .from('products')
        .select('stock_history')
        .eq('id', selectedStockProduct.id)
        .single();

      // Prepare new history entry
      const newHistoryEntry = {
        type: operationType,
        amount: stockAmount,
        previous_stock: selectedStockProduct.stock,
        new_stock: newStock,
        updated_at: new Date().toISOString(),
        updated_by: updaterName
      };

      // Update stock history
      const updatedHistory = [
        newHistoryEntry,
        ...(currentProduct?.stock_history || [])
      ].slice(0, 10);

      const { error } = await supabase
        .from('products')
        .update({
          stock: newStock,
          updated_at: new Date().toISOString(),
          updated_by: updaterName,
          stock_history: updatedHistory
        })
        .eq('id', selectedStockProduct.id);

      if (error) throw error;

      // Update local state
      setProducts(products.map(p => 
        p.id === selectedStockProduct.id 
          ? { 
              ...p, 
              stock: newStock, 
              updated_at: new Date().toISOString(), 
              updated_by: updaterName,
              stock_history: updatedHistory
            }
          : p
      ));

      // Save operation for undo
      setLastOperation({
        productId: selectedStockProduct.id,
        previous_stock: selectedStockProduct.stock,
        updated_by: updaterName,
        previous_history: currentProduct?.stock_history || []
      });

      // Show undo notification
      setShowUndoNotification(true);
      setUndoCountdown(10);

      // Close modal and reset states
      setShowStockModal(false);
      setSelectedStockProduct(null);
    } catch (err) {
      throw new Error('Stok güncellenirken bir hata oluştu: ' + err.message);
    }
  };

  // Add click handler for product rows/cards
  const handleProductClick = (product, event) => {
    // If clicking on action buttons or links, don't handle selection
    if (event.target.closest('button') || event.target.closest('a')) {
      return;
    }

    // If at least one product is selected, handle selection
    if (selectedProducts.length > 0) {
      handleSelectProduct(product.id, event);
    } else {
      // Find the current product's index in the filtered products
      const currentProductIndex = filteredProducts.findIndex(p => p.id === product.id);
      
      // Only show product details if no products are selected
      setSelectedProduct({
        ...product,
        currentIndex: currentProductIndex >= 0 ? currentProductIndex : 0
      });
    }
  };

  // Add navigation handlers for the ProductModal
  const handleNextProduct = () => {
    if (!selectedProduct) return;
    
    const currentIndex = selectedProduct.currentIndex;
    
    // Check if there's a next product
    if (currentIndex < filteredProducts.length - 1) {
      const nextProduct = filteredProducts[currentIndex + 1];
      setSelectedProduct({
        ...nextProduct,
        currentIndex: currentIndex + 1
      });
    }
  };

  const handlePreviousProduct = () => {
    if (!selectedProduct) return;
    
    const currentIndex = selectedProduct.currentIndex;
    
    // Check if there's a previous product
    if (currentIndex > 0) {
      const previousProduct = filteredProducts[currentIndex - 1];
      setSelectedProduct({
        ...previousProduct,
        currentIndex: currentIndex - 1
      });
    }
  };

  // Available options for items per page
  const itemsPerPageOptions = [5, 10, 25, 50, 100];

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
    setShowItemsPerPageMenu(false);
  };

  // Permission check functions
  const canAddStock = () => {
    return permissions.stok_add_permission;
  };

  const canEditStock = () => {
    return permissions.stok_edit_permission;
  };

  const canPerformStockMovements = () => {
    return permissions.stok_movement_permission;
  };

  const canDeleteStock = () => {
    return permissions.stok_delete_permission;
  };

  // Handle stock movement with permission check
  const handleStockMovement = (product) => {
    if (!canPerformStockMovements()) {
      toast.error('Stok Giriş/Çıkış İşlemi yapma yetkiniz bulunmamaktadır!', {
        duration: 3000,
        position: 'top-center',
        icon: '🔒',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }

    setSelectedStockProduct(product);
    setShowStockModal(true);
  };

  // Handle product edit with permission check
  const handleEditProduct = (productId, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!canEditStock()) {
      toast.error('Ürün düzenleme yetkiniz bulunmamaktadır!', {
        duration: 3000,
        position: 'top-center',
        icon: '🔒',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return false;
    }
    return true;
  };

  // Bulk Operations States
  const [showBulkOperationsModal, setShowBulkOperationsModal] = useState(false);
  const [bulkOperationType, setBulkOperationType] = useState('');
  const [bulkOperationData, setBulkOperationData] = useState({
    stockAmount: '',
    stockOperation: 'add',
    newLocation: '',
    newDescription: '',
    newProductLink: '',
    newImageUrl: '',
    selectedImageFile: null,
    imageUploadMethod: 'url' // 'url' or 'file'
  });

  // Image upload states for bulk operations
  const [dragActive, setDragActive] = useState(false);
  const bulkImageInputRef = useRef(null);

  // Progress and Results
  const [showBulkProgress, setShowBulkProgress] = useState(false);
  const [bulkOperationProgress, setBulkOperationProgress] = useState(0);
  const [bulkProcessedCount, setBulkProcessedCount] = useState(0);
  const [bulkTotalCount, setBulkTotalCount] = useState(0);
  const [bulkOperationResults, setBulkOperationResults] = useState([]);
  
  // Import/Export
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('');
  
  // Bulk Operations Functions
  const handleBulkOperation = (operationType) => {
    // Permission checks before allowing operations
    switch (operationType) {
      case 'stock_update':
        if (!permissions.stok_movement_permission) {
          toast.error('Stok güncelleme yetkiniz bulunmuyor!', {
            icon: '🚫',
            duration: 4000
          });
          return;
        }
        break;
      case 'location_update':
      case 'description_update':
      case 'product_link_update':
      case 'image_update':
        if (!permissions.stok_edit_permission) {
          toast.error('Ürün düzenleme yetkiniz bulunmuyor!', {
            icon: '🚫',
            duration: 4000
          });
          return;
        }
        break;
      case 'delete':
        if (!permissions.stok_delete_permission) {
          toast.error('Ürün silme yetkiniz bulunmuyor!', {
            icon: '🚫',
            duration: 4000
          });
          return;
        }
        break;
      default:
        toast.error('Geçersiz işlem türü!', {
          icon: '❌',
          duration: 3000
        });
        return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Lütfen işlem yapmak için ürün seçin');
      return;
    }

    setBulkOperationType(operationType);
    setShowBulkOperationsModal(true);
    
    // Success message with permission context
    const operationNames = {
      'stock_update': 'Stok Güncelleme',
      'location_update': 'Konum Güncelleme', 
      'description_update': 'Açıklama Güncelleme',
      'product_link_update': 'Link Güncelleme',
      'image_update': 'Görsel Güncelleme',
      'delete': 'Silme'
    };
    
    toast.success(`${operationNames[operationType]} işlemi başlatıldı`, {
      icon: '✅',
      duration: 2000
    });
  };

  const executeBulkOperation = async () => {
    // Double-check permissions before execution (security layer)
    switch (bulkOperationType) {
      case 'stock_update':
        if (!permissions.stok_movement_permission) {
          toast.error('Stok güncelleme yetkiniz bulunmuyor!', { icon: '🚫' });
          return;
        }
        break;
      case 'location_update':
      case 'description_update':
      case 'product_link_update':
      case 'image_update':
        if (!permissions.stok_edit_permission) {
          toast.error('Ürün düzenleme yetkiniz bulunmuyor!', { icon: '🚫' });
          return;
        }
        break;
      case 'delete':
        if (!permissions.stok_delete_permission) {
          toast.error('Ürün silme yetkiniz bulunmuyor!', { icon: '🚫' });
          return;
        }
        break;
      default:
        toast.error('Geçersiz işlem türü!', { icon: '❌' });
        return;
    }

    if (!bulkOperationType) {
      toast.error('Lütfen bir işlem türü seçin!');
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Lütfen en az bir ürün seçin!');
      return;
    }

    try {
      setShowBulkProgress(true);
      setBulkOperationProgress(0);
      setBulkProcessedCount(0);
      setBulkTotalCount(selectedProducts.length);
      setBulkOperationResults([]);

      const selectedProductsData = products.filter(p => selectedProducts.includes(p.id));
      const results = [];
      
      // Use userProfile for updater name
      let updaterName = 'Belirtilmemiş kullanıcı';
      if (userProfile) {
        updaterName = `${userProfile.first_name} ${userProfile.last_name}`;
      }

      // Image management for bulk image update
      let preUploadedImageUrl = null;
      let oldImageUrls = []; // Track old images for cleanup
      let successfullyUpdatedProducts = []; // Track successful updates for rollback
      
      if (bulkOperationType === 'image_update') {
        // Collect old image URLs from selected products for potential cleanup
        oldImageUrls = selectedProductsData
          .map(product => product.image_url)
          .filter(url => url && url.trim() !== '');
        
        // Remove duplicates
        oldImageUrls = [...new Set(oldImageUrls)];
        
        console.log('Old image URLs to potentially clean:', oldImageUrls);
      }

      // Pre-upload image if it's file upload for image_update operation
      if (bulkOperationType === 'image_update' && bulkOperationData.imageUploadMethod === 'file' && bulkOperationData.selectedImageFile) {
        try {
          setBulkOperationProgress(5); // Show progress for image upload
          preUploadedImageUrl = await uploadImage(bulkOperationData.selectedImageFile);
          toast.success('Görsel başarıyla yüklendi, şimdi ürünlere uygulanıyor...', {
            duration: 2000,
            position: 'top-center',
            icon: '📸',
          });
        } catch (uploadError) {
          console.error('Pre-upload failed:', uploadError);
          toast.error(`Görsel yükleme hatası: ${uploadError.message}`);
          setShowBulkProgress(false);
          return;
        }
      }

      const batchSize = 10;
      
      for (let i = 0; i < selectedProductsData.length; i += batchSize) {
        const batch = selectedProductsData.slice(i, i + batchSize);
        
        for (const product of batch) {
          try {
            const updateData = {
              updated_at: new Date().toISOString(),
              updated_by: updaterName
            };
            
            let historyEntry = null;
            
            switch (bulkOperationType) {
              case 'stock_update':
                const stockAmount = parseInt(bulkOperationData.stockAmount);
                let newStock = product.stock;
                
                if (bulkOperationData.stockOperation === 'add') {
                  newStock = product.stock + stockAmount;
                } else if (bulkOperationData.stockOperation === 'remove') {
                  newStock = Math.max(0, product.stock - stockAmount);
                } else if (bulkOperationData.stockOperation === 'set') {
                  newStock = stockAmount;
                }
                
                updateData.stock = newStock;
                
                historyEntry = {
                  type: bulkOperationData.stockOperation === 'set' ? 'update' : bulkOperationData.stockOperation,
                  amount: stockAmount,
                  previous_stock: product.stock,
                  new_stock: newStock,
                  updated_at: new Date().toISOString(),
                  updated_by: updaterName,
                  note: 'Toplu işlem'
                };
                
                // Update stock history
                const updatedHistory = [
                  historyEntry,
                  ...(product.stock_history || [])
                ].slice(0, 10);
                
                updateData.stock_history = updatedHistory;
                break;
                
              case 'location_update':
                updateData.location = bulkOperationData.newLocation;
                break;
                
              case 'description_update':
                updateData.description = bulkOperationData.newDescription;
                break;
                
              case 'category_update':
                updateData.category = bulkOperationData.newCategory;
                break;
                
              case 'price_update':
                updateData.price = parseFloat(bulkOperationData.newPrice);
                break;
                
              case 'product_link_update':
                if (bulkOperationData.newProductLink && bulkOperationData.newProductLink.trim()) {
                  updateData.purchase_link = bulkOperationData.newProductLink.trim();
                }
                break;
                
              case 'image_update':
                // Use pre-uploaded image URL or direct URL
                let finalImageUrl = null;
                
                if (bulkOperationData.imageUploadMethod === 'url' && bulkOperationData.newImageUrl && bulkOperationData.newImageUrl.trim()) {
                  finalImageUrl = bulkOperationData.newImageUrl.trim();
                } else if (bulkOperationData.imageUploadMethod === 'file' && preUploadedImageUrl) {
                  finalImageUrl = preUploadedImageUrl;
                }
                
                if (finalImageUrl) {
                  updateData.image_url = finalImageUrl;
                } else {
                  results.push({
                    productId: product.id,
                    productName: product.name,
                    status: 'skipped',
                    message: 'Geçerli görsel verisi bulunamadı'
                  });
                  continue;
                }
                break;
                
              case 'delete':
                // For delete operation, we don't update - we delete
                try {
                  // Delete associated image if exists
                  if (product.image_url) {
                    await deleteImageFromBucket(product.image_url);
                  }
                  
                  const { error } = await supabase
                    .from('products')
                    .delete()
                    .eq('id', product.id);
                    
                  if (error) {
                    console.error('Supabase delete error:', error);
                    throw new Error(`Silme hatası: ${error.message}`);
                  }
                  
                  results.push({
                    productId: product.id,
                    productName: product.name,
                    status: 'success',
                    message: 'Başarıyla silindi'
                  });
                  
                  // Update local state - remove deleted product
                  setProducts(prevProducts => 
                    prevProducts.filter(p => p.id !== product.id)
                  );
                  
                  continue; // Skip the update logic below
                } catch (error) {
                  results.push({
                    productId: product.id,
                    productName: product.name,
                    status: 'error',
                    message: error.message
                  });
                  continue;
                }
            }
            
            // Skip if no meaningful update data (only updated_at and updated_by)
            const meaningfulFields = Object.keys(updateData).filter(key => 
              key !== 'updated_at' && key !== 'updated_by'
            );
            
            if (meaningfulFields.length === 0) {
              results.push({
                productId: product.id,
                productName: product.name,
                status: 'skipped',
                message: 'Güncelleme için geçerli veri bulunamadı'
              });
              continue;
            }
            
            const { error } = await supabase
              .from('products')
              .update(updateData)
              .eq('id', product.id);
              
            if (error) {
              console.error('Supabase update error:', error);
              throw new Error(`Veritabanı hatası: ${error.message}`);
            }
            
            // Track successful image updates
            if (bulkOperationType === 'image_update') {
              successfullyUpdatedProducts.push({
                id: product.id,
                oldImageUrl: product.image_url,
                newImageUrl: updateData.image_url
              });
            }
            
            results.push({
              productId: product.id,
              productName: product.name,
              status: 'success',
              message: 'Başarıyla güncellendi'
            });
            
            // Update local state
            setProducts(prevProducts => 
              prevProducts.map(p => 
                p.id === product.id ? { ...p, ...updateData } : p
              )
            );
            
          } catch (error) {
            results.push({
              productId: product.id,
              productName: product.name,
              status: 'error',
              message: error.message
            });
          }
        }
        
        // Update progress
        const newCount = Math.min(i + batchSize, selectedProductsData.length);
        setBulkProcessedCount(newCount);
        
        // For image upload, start progress from 10% (after pre-upload)
        const baseProgress = bulkOperationType === 'image_update' && preUploadedImageUrl ? 10 : 0;
        const operationProgress = Math.floor(((newCount / selectedProductsData.length) * (100 - baseProgress)) + baseProgress);
        setBulkOperationProgress(operationProgress);
        
        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Post-processing for image cleanup
      if (bulkOperationType === 'image_update') {
        const successCount = results.filter(r => r.status === 'success').length;
        const errorCount = results.filter(r => r.status === 'error').length;
        
        // If some operations failed, handle rollback for newly uploaded image
        if (errorCount > 0 && preUploadedImageUrl) {
          const failedProducts = results.filter(r => r.status === 'error');
          
          // If ALL operations failed, delete the newly uploaded image
          if (successCount === 0) {
            console.log('All image updates failed, cleaning up newly uploaded image:', preUploadedImageUrl);
            try {
              await deleteImageFromBucket(preUploadedImageUrl);
              toast.error(`Tüm güncelleme işlemleri başarısız oldu. Yüklenen görsel temizlendi.`, {
                duration: 4000,
                position: 'top-center',
                icon: '🗑️',
              });
            } catch (cleanupError) {
              console.error('Failed to cleanup newly uploaded image:', cleanupError);
            }
          } else {
            // Some succeeded, some failed - warn user
            toast.warning(`${errorCount} ürün güncellenemedi. Bu ürünler için görsel bağlantısı kopuk olabilir.`, {
              duration: 5000,
              position: 'top-center',
              icon: '⚠️',
            });
          }
        }
        
        // Clean up old images that are no longer used (only if some updates were successful)
        if (successCount > 0 && oldImageUrls.length > 0) {
          console.log('Starting cleanup of old images...');
          
          // Check which old images are still in use by other products
          const allProducts = await supabase
            .from('products')
            .select('image_url')
            .not('image_url', 'is', null);
          
          if (allProducts.data) {
            const currentlyUsedImages = allProducts.data
              .map(p => p.image_url)
              .filter(url => url && url.trim() !== '');
            
            // Find old images that are no longer used anywhere
            const imagesToDelete = oldImageUrls.filter(oldUrl => {
              // Don't delete if it's the new image we just uploaded
              if (oldUrl === preUploadedImageUrl || oldUrl === bulkOperationData.newImageUrl) {
                return false;
              }
              
              // Check if this old image is still used by any product
              return !currentlyUsedImages.includes(oldUrl);
            });
            
            console.log('Images to delete:', imagesToDelete);
            
            // Delete unused old images
            for (const imageUrl of imagesToDelete) {
              try {
                await deleteImageFromBucket(imageUrl);
                console.log('Successfully deleted old image:', imageUrl);
              } catch (deleteError) {
                console.error('Failed to delete old image:', imageUrl, deleteError);
              }
            }
            
            if (imagesToDelete.length > 0) {
              toast.success(`${imagesToDelete.length} kullanılmayan eski görsel temizlendi`, {
                duration: 3000,
                position: 'top-center',
                icon: '🧹',
              });
            }
          }
        }
      }
      
      setBulkOperationResults(results);
      setBulkOperationProgress(100);
      
      // Show success message
      const successCount = results.filter(r => r.status === 'success').length;
      const errorCount = results.filter(r => r.status === 'error').length;
      
      if (bulkOperationType === 'image_update' && preUploadedImageUrl) {
        toast.success(`Görsel toplu güncelleme tamamlandı! ${successCount} ürüne aynı görsel uygulandı, ${errorCount} hatalı`, {
          duration: 4000,
          position: 'top-center',
          icon: '🖼️',
        });
      } else {
        toast.success(`Toplu işlem tamamlandı! ${successCount} başarılı, ${errorCount} hatalı`, {
          duration: 4000,
          position: 'top-center',
          icon: '✅',
        });
      }
      
      // İşlem tamamlandığında modalları kapat ve durumu temizle
      setTimeout(() => {
        setShowBulkProgress(false);
        setBulkOperationProgress(0);
        setBulkProcessedCount(0);
        setBulkOperationResults([]);
        
        // Başarılı işlemler varsa seçimleri temizle
        if (results.some(r => r.status === 'success')) {
          setSelectedProducts([]);
          setBulkOperationType('');
          setBulkOperationData({
            stockAmount: '',
            stockOperation: 'add',
            newLocation: '',
            newDescription: '',
            newProductLink: '',
            newImageUrl: '',
            selectedImageFile: null,
            imageUploadMethod: 'url'
          });
        }
      }, 2000);
      
    } catch (error) {
      console.error('Bulk operation error:', error);
      toast.error('Toplu işlem sırasında bir hata oluştu');
      
      // Hata durumunda da modalı kapat
      setTimeout(() => {
        setShowBulkProgress(false);
        setBulkOperationProgress(0);
        setBulkProcessedCount(0);
        setBulkOperationResults([]);
      }, 3000);
    }
  };

  // Export Functions
  const exportToCSV = (data, filename) => {
    const headers = ['ID', 'Ürün Adı', 'Açıklama', 'Stok', 'Konum', 'Oluşturma Tarihi', 'Oluşturan', 'Son Güncelleme', 'Güncelleyen'];
    const csvContent = [
      headers.join(','),
      ...data.map(product => [
        product.id,
        `"${product.name}"`,
        `"${product.description || ''}"`,
        product.stock,
        `"${product.location || ''}"`,
        product.created_at,
        `"${product.created_by || ''}"`,
        product.updated_at || '',
        `"${product.updated_by || ''}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = (data, filename) => {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Lütfen dışa aktarılacak ürünleri seçin!', {
        duration: 3000,
        position: 'top-center',
        icon: '⚠️',
      });
      return;
    }
    
    setIsExporting(true);
    
    try {
      const selectedProductsData = products.filter(p => selectedProducts.includes(p.id));
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      
      if (bulkOperationData.exportFormat === 'csv') {
        exportToCSV(selectedProductsData, `stok-verileri-${timestamp}.csv`);
      } else if (bulkOperationData.exportFormat === 'json') {
        exportToJSON(selectedProductsData, `stok-verileri-${timestamp}.json`);
      }
      
      toast.success(`${selectedProductsData.length} ürün başarıyla dışa aktarıldı!`, {
        duration: 3000,
        position: 'top-center',
        icon: '📥',
      });
      
    } catch (error) {
      toast.error('Dışa aktarma sırasında bir hata oluştu: ' + error.message, {
        duration: 4000,
        position: 'top-center',
        icon: '❌',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Import Functions
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (file.type === 'application/json') {
          const data = JSON.parse(e.target.result);
          setImportPreview(data.slice(0, 10)); // Show first 10 rows
        } else if (file.type === 'text/csv') {
          const text = e.target.result;
          const lines = text.split('\n');
          const headers = lines[0].split(',');
          const rows = lines.slice(1, 11).map(line => {
            const values = line.split(',');
            const obj = {};
            headers.forEach((header, index) => {
              obj[header.trim()] = values[index]?.trim() || '';
            });
            return obj;
          });
          setImportPreview(rows);
        }
      } catch (error) {
        toast.error('Dosya okuma hatası: ' + error.message, {
          duration: 4000,
          position: 'top-center',
          icon: '❌',
        });
      }
    };
    
    reader.readAsText(file);
  };

  const executeImport = async () => {
    if (!importFile || importPreview.length === 0) return;
    
    setShowBulkProgress(true);
    setBulkOperationProgress(0);
    setBulkProcessedCount(0);
    
    try {
      let updaterName = 'Sistem';
      if (userProfile) {
        updaterName = `${userProfile.first_name} ${userProfile.last_name}`;
      }
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          let importData = [];
          
          if (importFile.type === 'application/json') {
            importData = JSON.parse(e.target.result);
          } else if (importFile.type === 'text/csv') {
            const text = e.target.result;
            const lines = text.split('\n');
            const headers = lines[0].split(',').map(h => h.trim());
            importData = lines.slice(1).filter(line => line.trim()).map(line => {
              const values = line.split(',');
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = values[index]?.trim().replace(/"/g, '') || '';
              });
              return obj;
            });
          }
          
          const batchSize = 10;
          const results = [];
          
          for (let i = 0; i < importData.length; i += batchSize) {
            const batch = importData.slice(i, i + batchSize);
            
            for (const item of batch) {
              try {
                const productData = {
                  name: item.name || item['Ürün Adı'] || '',
                  description: item.description || item['Açıklama'] || '',
                  stock: parseInt(item.stock || item['Stok'] || 0),
                  location: item.location || item['Konum'] || '',
                  created_at: new Date().toISOString(),
                  created_by: updaterName,
                  stock_history: [{
                    type: 'create',
                    amount: parseInt(item.stock || item['Stok'] || 0),
                    previous_stock: 0,
                    new_stock: parseInt(item.stock || item['Stok'] || 0),
                    updated_at: new Date().toISOString(),
                    updated_by: updaterName,
                    note: 'İçe aktarma'
                  }]
                };
                
                const { data, error } = await supabase
                  .from('products')
                  .insert([productData])
                  .select()
                  .single();
                  
                if (error) throw error;
                
                results.push({
                  productName: productData.name,
                  status: 'success',
                  message: 'Başarıyla eklendi'
                });
                
                // Add to local state
                setProducts(prev => [...prev, data]);
                
              } catch (error) {
                results.push({
                  productName: item.name || item['Ürün Adı'] || 'Bilinmeyen',
                  status: 'error',
                  message: error.message
                });
              }
            }
            
            // Update progress
            const newCount = Math.min(i + batchSize, importData.length);
            setBulkProcessedCount(newCount);
            setBulkOperationProgress(Math.floor((newCount / importData.length) * 100));
            
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          setBulkOperationResults(results);
          setBulkOperationProgress(100);
          
          const successCount = results.filter(r => r.status === 'success').length;
          const errorCount = results.filter(r => r.status === 'error').length;
          
          toast.success(`İçe aktarma tamamlandı! ${successCount} başarılı, ${errorCount} hatalı`, {
            duration: 4000,
            position: 'top-center',
            icon: '✅',
          });
          
          // Refresh products
          fetchProducts();
          
        } catch (error) {
          toast.error('İçe aktarma sırasında bir hata oluştu: ' + error.message, {
            duration: 4000,
            position: 'top-center',
            icon: '❌',
          });
        }
      };
      
      reader.readAsText(importFile);
      
    } catch (error) {
      toast.error('İçe aktarma sırasında bir hata oluştu: ' + error.message, {
        duration: 4000,
        position: 'top-center',
        icon: '❌',
      });
    }
  };

  // Modal otomatik kapanma sistemi
  useEffect(() => {
    // Eğer modal açıkken hiç ürün seçili değilse modalı kapat
    if (showBulkOperationsModal && selectedProducts.length === 0) {
      setShowBulkOperationsModal(false);
      setBulkOperationType('');
      setBulkOperationData({
        stockAmount: '',
        stockOperation: 'add',
        newLocation: '',
        newDescription: '',
        newProductLink: '',
        newImageUrl: '',
        selectedImageFile: null,
        imageUploadMethod: 'url'
      });
    }
  }, [selectedProducts.length, showBulkOperationsModal]);

  // Sayfa değiştiğinde modal ve seçimleri temizle
  useEffect(() => {
    if (currentPage !== 1) {
      setSelectedProducts([]);
      setShowBulkOperationsModal(false);
      setBulkOperationType('');
    }
  }, [currentPage]);

  // Arama yapıldığında seçimleri temizle
  useEffect(() => {
    if (searchTerm || Object.keys(filterConfig).some(key => {
      const value = filterConfig[key];
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v !== '');
      }
      return value !== '' && value !== false;
    })) {
      setSelectedProducts([]);
      setShowBulkOperationsModal(false);
      setBulkOperationType('');
    }
  }, [searchTerm, filterConfig]);

  // ESC tuşu ile modal kapatma
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showBulkOperationsModal) {
          setShowBulkOperationsModal(false);
          setBulkOperationType('');
        }
        if (showImportModal) {
          setShowImportModal(false);
          setImportFile(null);
          setImportPreview([]);
        }
        if (showBulkProgress) {
          // Progress modal'ı sadece işlem tamamlandıysa kapat
          if (bulkOperationProgress === 100) {
            setShowBulkProgress(false);
            setBulkOperationProgress(0);
            setBulkProcessedCount(0);
            setBulkOperationResults([]);
          }
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showBulkOperationsModal, showImportModal, showBulkProgress, bulkOperationProgress]);

  // Bulk operations image handling functions
  const handleBulkImageDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleBulkImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // File size validation (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Dosya boyutu 50MB\'dan küçük olmalıdır');
        return;
      }
      
      // File type validation
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yüklenebilir');
        return;
      }
      
      setBulkOperationData(prev => ({
        ...prev,
        selectedImageFile: file,
        newImageUrl: '', // Clear URL when file is selected
        imageUploadMethod: 'file'
      }));
    }
  };

  const handleBulkImageFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // File size validation (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Dosya boyutu 50MB\'dan küçük olmalıdır');
        return;
      }
      
      // File type validation
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yüklenebilir');
        return;
      }
      
      setBulkOperationData(prev => ({
        ...prev,
        selectedImageFile: file,
        newImageUrl: '', // Clear URL when file is selected
        imageUploadMethod: 'file'
      }));
    }
  };

  const handleBulkImageUrlChange = (e) => {
    const { value } = e.target;
    setBulkOperationData(prev => ({
      ...prev,
      newImageUrl: value,
      selectedImageFile: null, // Clear file when URL is entered
      imageUploadMethod: 'url'
    }));
  };

  const handleBulkDropzoneClick = () => {
    bulkImageInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Dosya ismini kısaltma fonksiyonu
  const truncateFileName = (fileName, maxLength = 25) => {
    if (!fileName) return '';
    if (fileName.length <= maxLength) return fileName;
    
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
    
    return `${truncatedName}...${extension}`;
  };

  // Function to upload image to Supabase Storage
  const uploadImage = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `photos/${fileName}`; // photos klasörüne yükle
  
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
  
      if (error) throw error;
  
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);
  
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Bulk location change handler
  const handleBulkLocationChange = (e) => {
    const value = e.target.value;
    
    // If empty, allow it
    if (value === '') {
      setBulkOperationData(prev => ({
        ...prev,
        newLocation: ''
      }));
      return;
    }
    
    // Check first character - must be a letter
    if (value.length >= 1) {
      const firstChar = value.charAt(0);
      if (!/[a-zA-Z]/.test(firstChar)) {
        return; // Reject input if first character isn't a letter
      }
    }
    
    let formattedValue = '';
    
    // Process the input based on its length
    if (value.length === 1) {
      // First character should be uppercase letter
      formattedValue = value.toUpperCase() + '-';
    } else if (value.length === 2) {
      // Second character should be a hyphen
      if (value.charAt(1) !== '-') {
        // If user typed something other than hyphen, replace it with hyphen
        formattedValue = value.charAt(0).toUpperCase() + '-';
      } else {
        formattedValue = value.charAt(0).toUpperCase() + '-';
      }
    } else if (value.length >= 3 && value.length <= 4) {
      // Characters 3-4 should be digits
      const prefix = value.substring(0, 2); // A-
      const digits = value.substring(2);
      
      // Only allow digits after the hyphen
      if (!/^\d*$/.test(digits)) {
        // If input contains non-digits after hyphen, reject it
        return;
      }
      
      formattedValue = prefix + digits;
      
      // Automatically add underscore after two digits
      if (digits.length === 2) {
        formattedValue += '_';
      }
    } else if (value.length >= 5) {
      // Get the parts
      const prefix = value.substring(0, 2); // A-
      const digits = value.substring(2, 4);
      const underscore = value.substring(4, 5);
      const lastChar = value.substring(5, 6);
      
      // Validate underscore
      if (underscore !== '_') {
        // If 5th character isn't underscore, make it one
        formattedValue = prefix + digits + '_';
      } else {
        formattedValue = prefix + digits + '_';
        
        // Last character must be 1, 2, or 3
        if (lastChar && !['1', '2', '3'].includes(lastChar)) {
          // If last character isn't valid, reject it
          return;
        }
        
        if (lastChar) {
          formattedValue += lastChar;
        }
      }
    }
    
    setBulkOperationData(prev => ({
      ...prev,
      newLocation: formattedValue
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Add scrollbar styles to DOM */}
      <style>{scrollbarStyles}</style>
      
      {/* Add animation delay styles */}
      <style>{`
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div className="flex items-center">
            <Package2 className="w-8 h-8 text-gray-800 dark:text-white mr-3" />
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Stok Yönetimi</h1>
          </div>
          
          {/* Enhanced Permission Indicator */}
          <div className="flex items-center">
            <div className="relative group">
              <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition-all duration-200">
                <Shield className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Yetkinlikleriniz</span>
                <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 ml-1" />
              </button>
              
              {/* Tooltip/Popover panel */}
              <div className="absolute right-0 mt-2 w-72 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 z-30 transition-all duration-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Stok Yönetim Yetkinlikleri</h3>
                    <div className="px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                      {Object.values(permissions).filter(Boolean).length}/{Object.values(permissions).length} Aktif
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Ekleme permission */}
                    <div className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        permissions.stok_add_permission 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                        <Plus className="w-4 h-4" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Ürün Ekleme</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {permissions.stok_add_permission 
                            ? 'Yeni ürünleri stok sistemine ekleyebilirsiniz' 
                            : 'Yeni ürün ekleme yetkiniz bulunmuyor'}
                        </p>
                      </div>
                      <div className="ml-auto">
                        {permissions.stok_add_permission 
                          ? <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" /> 
                          : <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />}
                      </div>
                    </div>
                    
                    {/* Düzenleme permission */}
                    <div className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        permissions.stok_edit_permission 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                        <Edit2 className="w-4 h-4" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Ürün Düzenleme</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {permissions.stok_edit_permission 
                            ? 'Mevcut ürünleri düzenleyebilirsiniz' 
                            : 'Ürün düzenleme yetkiniz bulunmuyor'}
                        </p>
                      </div>
                      <div className="ml-auto">
                        {permissions.stok_edit_permission 
                          ? <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" /> 
                          : <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />}
                      </div>
                    </div>
                    
                    {/* Giriş/Çıkış permission */}
                    <div className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        permissions.stok_movement_permission 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Stok Hareketi</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {permissions.stok_movement_permission 
                            ? 'Ürün stok giriş/çıkışı yapabilirsiniz' 
                            : 'Stok hareketi yapma yetkiniz bulunmuyor'}
                        </p>
                      </div>
                      <div className="ml-auto">
                        {permissions.stok_movement_permission 
                          ? <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" /> 
                          : <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />}
                      </div>
                    </div>
                    
                    {/* Silme permission */}
                    <div className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        permissions.stok_delete_permission 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                        <Trash2 className="w-4 h-4" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Ürün Silme</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {permissions.stok_delete_permission 
                            ? 'Ürünleri sistemden silebilirsiniz' 
                            : 'Ürün silme yetkiniz bulunmuyor'}
                        </p>
                      </div>
                      <div className="ml-auto">
                        {permissions.stok_delete_permission 
                          ? <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" /> 
                          : <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Yetki değişikliği için yöneticinizle iletişime geçin
                      </p>
                      <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Permission Pills - Condensed View */}
            <div className="flex ml-3 space-x-1.5 overflow-x-auto">
              {Object.entries(permissions).map(([key, value], index) => {
                // Get the appropriate icon based on permission type
                let Icon = Plus; // Default icon
                let label = 'Yetki';
                
                if (key === 'stok_add_permission') {
                  Icon = Plus;
                  label = 'Ekleme';
                } else if (key === 'stok_edit_permission') {
                  Icon = Edit2;
                  label = 'Düzenleme';
                } else if (key === 'stok_movement_permission') {
                  Icon = ArrowUpDown;
                  label = 'Giriş/Çıkış';
                } else if (key === 'stok_delete_permission') {
                  Icon = Trash2;
                  label = 'Silme';
                }
                
                return (
                  <div 
                    key={key}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                      value 
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-700 dark:text-green-400' 
                        : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                    } transition-all hover:shadow-sm`}
                    title={value ? `${label} yetkiniz bulunuyor` : `${label} yetkiniz bulunmuyor`}
                  >
                    <Icon className="w-3 h-3" />
                    <span className="text-xs font-medium">{label}</span>
                    {value ? (
                      <span className="w-3 h-3 flex items-center justify-center bg-green-200 dark:bg-green-700 rounded-full">
                        <span className="text-[8px] font-bold text-green-800 dark:text-green-200">✓</span>
                      </span>
                    ) : (
                      <span className="w-3 h-3 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-full">
                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-400">×</span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        {/* Search and Filter Toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          
          <div className="flex flex-col md:flex-row gap-4">
            {/* Enhanced Search */}
            <div className="flex-1">
              <div className="relative group" ref={searchInputRef}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className={`w-5 h-5 ${searchTerm ? 'text-green-500 dark:text-green-400' : 'text-gray-400'}`} />
                </div>
                <input
                  type="text"
                  placeholder="Ürün adı, konum, açıklama veya ekleyen kişi ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => recentSearches.length > 0 && setShowRecentSearches(true)}
                  className={`w-full pl-10 pr-24 py-3 border-2  ${
                    searchTerm 
                      ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20 ' 
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 '
                  } rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent text-gray-900 dark:text-white transition-all duration-200`}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
                {/* Search results count 
                {searchTerm && filteredProducts.length > 0 && (
                  <div className="absolute right-12 top-1/2 transform -translate-y-1/2 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded-full">
                    {filteredProducts.length} sonuç bulundu
                  </div>
                )}
                */}
                {/* Searching animation */}
                {isSearching && (
                  <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
                    <div className="animate-pulse flex space-x-1">
                      <div className="h-2 w-2 bg-green-400 dark:bg-green-500 rounded-full"></div>
                      <div className="h-2 w-2 bg-green-400 dark:bg-green-500 rounded-full animation-delay-200"></div>
                      <div className="h-2 w-2 bg-green-400 dark:bg-green-500 rounded-full animation-delay-400"></div>
                    </div>
                  </div>
                )}
                
                {/* Recent searches dropdown */}
                {showRecentSearches && recentSearches.length > 0 && (
                  <div className="absolute mt-1 w-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg z-10">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Son Aramalar</span>
                      <button 
                        onClick={() => {
                          setRecentSearches([]);
                          localStorage.removeItem('stockManagementRecentSearches');
                          setShowRecentSearches(false);
                        }}
                        className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Temizle
                      </button>
                    </div>
                    <ul className="py-1">
                      {recentSearches.map((term, index) => (
                        <li key={index}>
                          <div className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 group">
                            <button
                              onClick={() => {
                                setSearchTerm(term);
                                setShowRecentSearches(false);
                              }}
                              className="flex items-center overflow-hidden flex-grow text-left"
                            >
                              <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                              <span className="truncate max-w-[75%]" title={term}>{term}</span>
                            </button>
                            <button 
                              onClick={(e) => {
                                removeSearchTerm(term);
                              }}
                              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Bu aramayı sil"
                            >
                              <X className="w-3 h-3 text-gray-400 hover:text-red-500 dark:hover:text-red-400" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {/* Search results count 
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center pl-2">
                <span>{searchTerm ? 'Arama sonuçları' : 'Gelişmiş arama'}: ilk {Math.min(5, filteredProducts.length)} ürün vurgulanır</span>
                {searchTerm && (
                  <div className="ml-2 flex space-x-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-yellow-300 dark:bg-yellow-500"></span>
                    <span className="inline-block w-2 h-2 rounded-full bg-green-300 dark:bg-green-500"></span>
                  </div>
                )}
              </div>
              */}
            </div>

            {/* View Mode and Filter Toggles */}
            <div className="flex gap-3">
              {/* View Mode Toggle */}
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 rounded-md flex items-center gap-1 ${
                    viewMode === 'table' 
                      ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white shadow-sm' 
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <List className="w-4 h-4" />
                  {/*<span className="text-sm">Tablo</span> */}
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`px-3 py-1 rounded-md flex items-center gap-1 ${
                    viewMode === 'card' 
                      ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white shadow-sm' 
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  {/*<span className="text-sm">Kart</span> */}
                </button>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isFilterActive()
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
                <span>Filtreler</span>
                {getActiveFilterCount() > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-green-600 rounded-full ml-1">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <FilterPanel />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center pl-2">
                <span>{searchTerm ? 'Arama sonuçları' : 'Gelişmiş arama'}: ilk {Math.min(5, filteredProducts.length)} ürün vurgulanır</span>
                {searchTerm && (
                  <div className="ml-2 flex space-x-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-yellow-300 dark:bg-yellow-500"></span>
                    <span className="inline-block w-2 h-2 rounded-full bg-green-300 dark:bg-green-500"></span>
                  </div>
                )}
              </div>
              <br />
        {/* Active Filters Display */}
        {isFilterActive() && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Aktif Filtreler:</span>
            
            {filterConfig.showLowStock && (
              <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full flex items-center">
                Düşük Stok
                <button onClick={() => handleFilterChange('showLowStock', false)} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.showOutOfStock && (
              <span className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full flex items-center">
                Stokta Yok
                <button onClick={() => handleFilterChange('showOutOfStock', false)} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.showWithImage && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full flex items-center">
                Görselli
                <button onClick={() => handleFilterChange('showWithImage', false)} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.showWithoutImage && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full flex items-center">
                Görselsiz
                <button onClick={() => handleFilterChange('showWithoutImage', false)} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.locationPrefix && (
              <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full flex items-center">
                Konum: {filterConfig.locationPrefix}
                <button onClick={() => handleFilterChange('locationPrefix', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {(filterConfig.dateRange.from || filterConfig.dateRange.to) && (
              <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full flex items-center">
                Tarih: {filterConfig.dateRange.from ? new Date(filterConfig.dateRange.from).toLocaleDateString() : '...'} 
                - 
                {filterConfig.dateRange.to ? new Date(filterConfig.dateRange.to).toLocaleDateString() : '...'}
                <button onClick={() => handleFilterChange('dateRange', { from: '', to: '' })} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.createdBy && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full flex items-center">
                Ekleyen: {filterConfig.createdBy}
                <button onClick={() => handleFilterChange('createdBy', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.stockRange.min !== '' && (
              <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200 rounded-full flex items-center">
                Stok Aralığı: {filterConfig.stockRange.min} - {filterConfig.stockRange.max}
                <button onClick={() => handleFilterChange('stockRange', { ...filterConfig.stockRange, min: '' })} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.stockSort !== '' && (
              <span className="px-2 py-1 text-xs bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 rounded-full flex items-center">
                Stok Miktarına Göre Sırala: {filterConfig.stockSort === 'asc' ? 'Azdan Çoğa' : 'Çoktan Aza'}
                <button onClick={() => handleFilterChange('stockSort', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {filterConfig.dateSort !== '' && (
              <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded-full flex items-center">
                Tarihe Göre Sırala: {filterConfig.dateSort === 'newest' ? 'En Yeni' : 'En Eski'}
                <button onClick={() => handleFilterChange('dateSort', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            <button
              onClick={resetFilters}
              className="ml-auto text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Tümünü Temizle
            </button>
          </div>
        )}

        {/* Enhanced Bulk Actions */}
        {selectedProducts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-2xl border border-blue-200 dark:border-blue-800 shadow-lg mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-full">
                  <CheckSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Toplu İşlemler
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {selectedProducts.length} ürün seçildi
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedProducts([]);
                  setShowBulkOperationsModal(false);
                  setBulkOperationType('');
                  setBulkOperationData({
                    stockAmount: '',
                    stockOperation: 'add',
                    newLocation: '',
                    newDescription: '',
                    newProductLink: '',
                    newImageUrl: '',
                    selectedImageFile: null,
                    imageUploadMethod: 'url'
                  });
                  toast.success('Seçimler temizlendi');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                <span className="text-sm font-medium">Temizle</span>
              </motion.button>
            </div>

            {/* Permission Check Warning */}
            {!permissions.stok_edit_permission && !permissions.stok_movement_permission && !permissions.stok_delete_permission && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <div>
                    <h4 className="font-medium text-amber-800 dark:text-amber-200">Yetkisiz Erişim</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Toplu işlemler için gerekli yetkiniz bulunmuyor. Yöneticinizle iletişime geçin.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons Grid - With Permission Checks */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              {/* Stock Update - Requires stok_movement_permission */}
              {permissions.stok_movement_permission && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleBulkOperation('stock_update')}
                  className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Package className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-center">Stok Güncelle</span>
                  </div>
                </motion.button>
              )}

              {/* Location Update - Requires stok_edit_permission */}
              {permissions.stok_edit_permission && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleBulkOperation('location_update')}
                  className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <MapPin className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-center">Konum Güncelle</span>
                  </div>
                </motion.button>
              )}

              {/* Description Update - Requires stok_edit_permission */}
              {permissions.stok_edit_permission && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleBulkOperation('description_update')}
                  className="p-4 bg-gradient-to-br from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <FileText className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-center">Açıklama Güncelle</span>
                  </div>
                </motion.button>
              )}

              {/* Product Link Update - Requires stok_edit_permission */}
              {permissions.stok_edit_permission && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleBulkOperation('product_link_update')}
                  className="p-4 bg-gradient-to-br from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <LinkIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-center">Link Güncelle</span>
                  </div>
                </motion.button>
              )}

              {/* Image Update - Requires stok_edit_permission */}
              {permissions.stok_edit_permission && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleBulkOperation('image_update')}
                  className="p-4 bg-gradient-to-br from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <ImageIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-center">Görsel Güncelle</span>
                  </div>
                </motion.button>
              )}

              {/* Delete - Requires stok_delete_permission */}
              {permissions.stok_delete_permission && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleBulkDelete}
                  className="p-4 bg-gradient-to-br from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Trash2 className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-center">Sil</span>
                  </div>
                </motion.button>
              )}

              {/* Disabled buttons for unauthorized operations */}
              {!permissions.stok_movement_permission && (
                <div className="p-4 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-xl shadow-sm opacity-50 cursor-not-allowed">
                  <div className="flex flex-col items-center space-y-2">
                    <Package className="w-6 h-6" />
                    <span className="text-xs font-medium text-center">Stok Güncelle</span>
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span className="text-xs">Yetkisiz</span>
                    </div>
                  </div>
                </div>
              )}

              {!permissions.stok_edit_permission && (
                <>
                  <div className="p-4 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-xl shadow-sm opacity-50 cursor-not-allowed">
                    <div className="flex flex-col items-center space-y-2">
                      <MapPin className="w-6 h-6" />
                      <span className="text-xs font-medium text-center">Konum Güncelle</span>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-xs">Yetkisiz</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-xl shadow-sm opacity-50 cursor-not-allowed">
                    <div className="flex flex-col items-center space-y-2">
                      <FileText className="w-6 h-6" />
                      <span className="text-xs font-medium text-center">Açıklama Güncelle</span>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-xs">Yetkisiz</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-xl shadow-sm opacity-50 cursor-not-allowed">
                    <div className="flex flex-col items-center space-y-2">
                      <LinkIcon className="w-6 h-6" />
                      <span className="text-xs font-medium text-center">Link Güncelle</span>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-xs">Yetkisiz</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-xl shadow-sm opacity-50 cursor-not-allowed">
                    <div className="flex flex-col items-center space-y-2">
                      <ImageIcon className="w-6 h-6" />
                      <span className="text-xs font-medium text-center">Görsel Güncelle</span>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="text-xs">Yetkisiz</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!permissions.stok_delete_permission && (
                <div className="p-4 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-xl shadow-sm opacity-50 cursor-not-allowed">
                  <div className="flex flex-col items-center space-y-2">
                    <Trash2 className="w-6 h-6" />
                    <span className="text-xs font-medium text-center">Sil</span>
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span className="text-xs">Yetkisiz</span>
                    </div>
                  </div>
                </div>
              )}

              
            </div>

            {/* Export/Import Section - Always available for viewing data */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleExport('csv')}
                className="px-4 py-2 bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 rounded-lg transition-colors flex items-center gap-2"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm font-medium">CSV Dışa Aktar</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleExport('json')}
                className="px-4 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/40 rounded-lg transition-colors flex items-center gap-2"
              >
                <FileJson className="w-4 h-4" />
                <span className="text-sm font-medium">JSON Dışa Aktar</span>
              </motion.button>
              {/* Import requires add permission */}
              {permissions.stok_add_permission ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowImportModal(true)}
                  className="px-4 py-2 bg-teal-100 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/40 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-medium">İçe Aktar</span>
                </motion.button>
              ) : (
                <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-black dark:text-white rounded-lg cursor-not-allowed flex items-center gap-2 opacity-50">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-medium">İçe Aktar</span>
                  <AlertTriangle className="w-3 h-3" />
                </div>
              )}
            </div>

            {/* Permission Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  Aktif Yetkiler: {Object.values(permissions).filter(Boolean).length}/{Object.values(permissions).length}
                </span>
                <div className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  <span>Güvenli İşlem</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Products View (Table or Card) */}
        {viewMode === 'table' ? (
          // Table View
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="w-12 pl-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === filteredProducts.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProducts(filteredProducts.map(p => p.id));
                          } else {
                            setSelectedProducts([]);
                          }
                        }}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-50 dark:focus:ring-offset-gray-700"
                      />
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('name')}
                        className="group inline-flex items-center space-x-2 text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        <span>Ürün Adı</span>
                        <div className="w-4">
                          {sortConfig.key === 'name' ? (
                            sortConfig.direction === 'asc' ? (
                              <ChevronUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-green-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-4 h-4 opacity-0 group-hover:opacity-50" />
                          )}
                        </div>
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('location')}
                        className="group inline-flex items-center space-x-2 text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        <span>Konum</span>
                        <div className="w-4">
                          {sortConfig.key === 'location' ? (
                            sortConfig.direction === 'asc' ? (
                              <ChevronUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-green-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-4 h-4 opacity-0 group-hover:opacity-50" />
                          )}
                        </div>
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('stock')}
                        className="group inline-flex items-center space-x-2 text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        <span>Stok</span>
                        <div className="w-4">
                          {sortConfig.key === 'stock' ? (
                            sortConfig.direction === 'asc' ? (
                              <ChevronUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-green-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-4 h-4 opacity-0 group-hover:opacity-50" />
                          )}
                        </div>
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        İşlemler
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <Package2 className="w-8 h-8 text-gray-400" />
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            <p className="font-medium">Ürün bulunamadı</p>
                            <p className="mt-1">Filtreleri değiştirerek tekrar deneyin</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((product) => (
                      <tr 
                        key={product.id} 
                        className={`group hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${
                          selectedProducts.includes(product.id) ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                        }`}
                        onClick={(e) => handleProductClick(product, e)}
                      >
                        <td className="pl-6 py-4 whitespace-nowrap w-12">
<input
  type="checkbox"
  checked={selectedProducts.includes(product.id)}
  onChange={(e) => handleSelectProduct(product.id, e)}
  onClick={(e) => e.stopPropagation()}
  className="w-4 h-4 border-gray-300 rounded-[12px] appearance-none checked:bg-green-600 checked:border-green-600 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 dark:bg-gray-800"
/>

                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package2 className="w-5 h-5 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div>
                              <h3 
                                className={`text-lg font-medium cursor-pointer ${
                                  searchTerm && product.searchVisualMatch && product.searchMatches?.includes('name') 
                                    ? 'bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded text-gray-800 dark:text-yellow-50' 
                                    : 'text-gray-900 dark:text-white'
                                }`}
                                onClick={(e) => {
                                  if (selectedProducts.length > 0) {
                                    handleProductClick(product, e);
                                  } else {
                                    setSelectedProduct({
                                      ...product,
                                      currentIndex: filteredProducts.findIndex(p => p.id === product.id) >= 0 ? 
                                        filteredProducts.findIndex(p => p.id === product.id) : 0
                                    });
                                  }
                                }}
                              >
                                {product.name}
                                {searchTerm && product.searchVisualMatch && product.searchMatches?.includes('name') && (
                                  <span className="ml-1 text-xs text-yellow-600 dark:text-yellow-400 font-normal">(ad)</span>
                                )}
                                {searchTerm && product.searchVisualMatch && product.searchScore && product.searchMatches?.includes('name') && (
                                  <span className="ml-1 text-xs text-green-600 dark:text-green-400 font-normal">
                                    ({Math.round(product.searchScore / 10)}/10)
                                  </span>
                                )}
                              </h3>
                              {product.description && (
                                <div className={`text-sm text-gray-500 dark:text-gray-400 line-clamp-1 max-w-md ${searchTerm && product.searchVisualMatch && product.searchMatches?.includes('description') ? 'bg-green-50 dark:bg-green-900/30 px-1 rounded' : ''}`}>
                                  {product.description}
                                  {searchTerm && product.searchVisualMatch && product.searchMatches?.includes('description') && (
                                    <span className="ml-1 text-xs text-yellow-600 dark:text-yellow-400 font-normal">(açıklama)</span>
                                  )}
                                </div>
                              )}
                              {searchTerm && product.searchVisualMatch && product.searchMatches && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {product.searchMatches?.length > 0 && (
                                    <span className="italic">
                                      Eşleşen {product.searchMatches.length > 1 ? 'alanlar' : 'alan'}: {' '}
                                      {product.searchMatches.map(match => 
                                        match === 'name' ? 'Ad' : 
                                        match === 'location' ? 'Konum' : 
                                        match === 'description' ? 'Açıklama' : 
                                        'Ekleyen'
                                      ).join(', ')}
                                      {product.searchScore && (
                                        <span className="ml-2 text-green-600 dark:text-green-400">
                                          ({Math.round(product.searchScore / 10)}/10)
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                            {product.location}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {product.stock === 0 ? (
                              <div className="flex items-center space-x-1.5 text-red-600 dark:text-red-400">
                                <XCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">Stokta Yok</span>
                              </div>
                            ) : product.stock < 10 ? (
                              <div className="flex items-center space-x-1.5 text-yellow-600 dark:text-yellow-400">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">{product.stock} Adet</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1.5 text-green-600 dark:text-green-400">
                                <CheckCircle2 className="w-4 h-4" />
                                <span className="text-sm font-medium">{product.stock} Adet</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end -space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProduct(product);
                              }}
                              className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group/button"
                            >
                              <Info className="w-4 h-4 text-gray-400 group-hover/button:text-blue-500" />
                              <span className="sr-only">Detaylar</span>
                              <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-blue-500 opacity-0 group-hover/button:opacity-100 transition-opacity"></span>
                            </button>
                            <button
  onClick={e => {
    e.stopPropagation();
    handleStockMovement(product);
  }}
                              className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group/button"
                            >
                              <ArrowUpDown className="w-4 h-4 text-gray-400 group-hover/button:text-green-500" />
                              <span className="sr-only">Stok İşlemleri</span>
                              <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-green-500 opacity-0 group-hover/button:opacity-100 transition-opacity"></span>
                            </button>
                            <Link
                              to={`/stokflow/product/edit/${product.id}`}
                              className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group/button"
                              onClick={e => {
                                e.stopPropagation();
                                if (!handleEditProduct(product.id, e)) {
                                  e.preventDefault();  // izin yoksa yönlendirmeyi iptal et
                                }
                              }}
                            >
                              <Edit2 className="w-4 h-4 text-gray-400 group-hover/button:text-blue-500" />
                              <span className="sr-only">Düzenle</span>
                              <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-blue-500 opacity-0 group-hover/button:opacity-100 transition-opacity"></span>
                            </Link>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(product.id);
                              }}
                              className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group/button"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400 group-hover/button:text-red-500" />
                              <span className="sr-only">Sil</span>
                              <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-red-500 opacity-0 group-hover/button:opacity-100 transition-opacity"></span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination />
          </div>
        ) : (
          // Card View
          <div>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-300">Yükleniyor...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
                <Package2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white animate-pulse">Ürün bulunamadı</h3>
                <p className="mt-1 text-gray-500 dark:text-gray-400 animate-pulse">Filtreleri değiştirerek tekrar deneyin veya yeni ürün ekleyin.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentItems.map((product) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer ${
                        selectedProducts.includes(product.id) ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''
                      }`}
                      onClick={(e) => handleProductClick(product, e)}
                    >
                      {/* Card Header with Image */}
                      <div 
                        className="relative h-48 bg-gray-100 dark:bg-gray-700 cursor-pointer" 
                        onClick={(e) => {
                          if (selectedProducts.length > 0) {
                            handleProductClick(product, e);
                          } else {
                            setSelectedProduct({
                              ...product,
                              currentIndex: filteredProducts.findIndex(p => p.id === product.id) >= 0 ? 
                                filteredProducts.findIndex(p => p.id === product.id) : 0
                            });
                          }
                        }}
                      >
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package2 className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        
                        {/* Selection Checkbox */}
                        <div className="absolute top-2 left-2">
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product.id)}
                            onChange={(e) => handleSelectProduct(product.id, e)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-800"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      
                      {/* Card Content */}
                      <div 
                        className="p-4" 
                        onClick={(e) => {
                          if (selectedProducts.length > 0) {
                            handleProductClick(product, e);
                          } else {
                            setSelectedProduct({
                              ...product,
                              currentIndex: filteredProducts.findIndex(p => p.id === product.id) >= 0 ? 
                                filteredProducts.findIndex(p => p.id === product.id) : 0
                            });
                          }
                        }}
                      >
                        <div className="mb-2 flex justify-between items-start">
                          <h3 
                            className={`text-lg font-medium cursor-pointer ${
                              searchTerm && product.searchVisualMatch && product.searchMatches?.includes('name') 
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded text-gray-800 dark:text-yellow-50' 
                                : 'text-gray-900 dark:text-white'
                            }`}
                            onClick={(e) => {
                              if (selectedProducts.length > 0) {
                                handleProductClick(product, e);
                              } else {
                                setSelectedProduct({
                                  ...product,
                                  currentIndex: filteredProducts.findIndex(p => p.id === product.id) >= 0 ? 
                                    filteredProducts.findIndex(p => p.id === product.id) : 0
                                });
                              }
                            }}
                          >
                            {product.name}
                            {searchTerm && product.searchVisualMatch && product.searchMatches?.includes('name') && (
                              <span className="ml-1 text-xs text-yellow-600 dark:text-yellow-400 font-normal">(ad)</span>
                            )}
                            {searchTerm && product.searchVisualMatch && product.searchScore && product.searchMatches?.includes('name') && (
                              <span className="ml-1 text-xs text-green-600 dark:text-green-400 font-normal">
                                ({Math.round(product.searchScore / 10)}/10)
                              </span>
                            )}
                          </h3>
                          <span className={`px-2 py-1 text-xs leading-none font-semibold rounded-full ${
                            searchTerm && product.searchVisualMatch && product.searchMatches?.includes('location') 
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' 
                              : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                          }`}>
                            {product.location}
                            {searchTerm && product.searchVisualMatch && product.searchMatches?.includes('location') && (
                              <span className="ml-1 text-xs text-yellow-600 dark:text-yellow-400 font-normal">(konum)</span>
                            )}
                          </span>
                        </div>
                        
                        {/* Description (Truncated) */}
                        {product.description && (
                          <p className={`text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4 ${
                            searchTerm && product.searchVisualMatch && product.searchMatches?.includes('description') 
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 px-1 rounded' 
                              : ''
                          }`}>
                            {product.description}
                            {searchTerm && product.searchVisualMatch && product.searchMatches?.includes('description') && (
                              <span className="ml-1 text-xs text-yellow-600 dark:text-yellow-400 font-normal">(açıklama)</span>
                            )}
                          </p>
                        )}
                        
                        {/* Search Results */}
                        {searchTerm && product.searchVisualMatch && product.searchMatches && product.searchMatches.length > 0 && (
                          <div className="flex items-center p-1 mt-1 bg-gray-50 dark:bg-gray-700/50 rounded-md mb-4">
                            <span className="font-medium text-blue-600 dark:text-blue-400 text-xs mr-1">Eşleşme: </span>
                            <span className="text-xs">
                              {product.searchMatches.map(match => 
                                match === 'name' ? 'Ad' : 
                                match === 'location' ? 'Konum' : 
                                match === 'description' ? 'Açıklama' : 
                                'Ekleyen'
                              ).join(', ')}
                            </span>
                            {product.searchScore && (
                              <span className="ml-auto bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded text-green-700 dark:text-green-300 font-semibold text-xs">
                                %{Math.min(Math.round(product.searchScore / 7), 100)} benzerlik
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Stock Status */}
                        <div className="flex items-center mb-4">
                          {product.stock === 0 ? (
                            <XCircle className="w-4 h-4 text-red-500 mr-2" />
                          ) : product.stock < 10 ? (
                            <AlertCircle className="w-4 h-4 text-yellow-500 mr-2" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                          )}
                          <span className={`text-sm font-medium ${
                            product.stock === 0
                              ? 'text-red-600 dark:text-red-400'
                              : product.stock < 10
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {product.stock} Adet Mevcut
                          </span>
                        </div>
                        
                        {/* Metadata */}
                        <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-4">
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(product.created_at).toLocaleDateString('tr-TR')}
                          </div>
                          {product.created_by && (
                            <div className="flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              {product.created_by}
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex justify-end gap-2 border-t dark:border-gray-700 pt-3">
                          <button
                            onClick={() => setSelectedProduct({
                              ...product,
                              currentIndex: filteredProducts.findIndex(p => p.id === product.id) >= 0 ? 
                                filteredProducts.findIndex(p => p.id === product.id) : 0
                            })}
                            className="p-1 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                            title="Detayları Görüntüle"
                          >
                            <Info className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStockProduct(product);
                              setShowStockModal(true);
                            }}
                            className="p-1 rounded-full hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400"
                            title="Stok İşlemleri"
                          >
                            <ArrowUpDown className="w-5 h-5" />
                          </button>
                          <Link
                            to={`/stokflow/product/edit/${product.id}`}
                            className="p-1 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                            title="Düzenle"
                            onClick={e => {
                              e.stopPropagation();
                              if (!handleEditProduct(product.id, e)) {
                                e.preventDefault();  // izin yoksa yönlendirmeyi iptal et
                              }
                            }}
                          >
                            <Edit2 className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(product.id);
                            }}
                            className="p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                            title="Sil"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-6">
                  <Pagination />
                </div>
              </>
            )}
          </div>
        )}

        {/* Product Modal */}
        {selectedProduct && (
          <ProductModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            onNext={handleNextProduct}
            onPrevious={handlePreviousProduct}
            hasNext={selectedProduct.currentIndex < filteredProducts.length - 1}
            hasPrevious={selectedProduct.currentIndex > 0}
          />
        )}

        {/* Add Delete Confirmation Modal */}
        <DeleteConfirmationModal />

        {/* Add Stock Management Modal */}
        <StockManagementModal
          isOpen={showStockModal}
          onClose={() => {
            setShowStockModal(false);
            setSelectedStockProduct(null);
          }}
          selectedProduct={selectedStockProduct}
          onStockUpdate={handleStockUpdate}
          userProfile={userProfile}
        />

        {/* Undo Notification */}
        <AnimatePresence>
          {showUndoNotification && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 max-w-sm w-full border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1 flex items-center">
                    <ArrowUpDown className="w-5 h-5 text-blue-500 dark:text-blue-400 mr-2" />
                    Son Stok İşlemi
                  </h4>

                  {lastOperation && (
                    <>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-2 mb-3">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                          {products.find(p => p.id === lastOperation?.productId)?.name}
                        </p>
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-blue-600 dark:text-blue-400">{lastOperation?.previous_stock}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          <span className="font-medium text-blue-600 dark:text-blue-400">{products.find(p => p.id === lastOperation?.productId)?.stock}</span>
                          <span className="ml-1">Adet</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          <span>
                            {new Date().toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          <User className="w-3 h-3 ml-2 mr-1" />
                          <span>{lastOperation?.updated_by}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <button
                          onClick={handleUndoOperation}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          İşlemi Geri Al
                        </button>
                        
                        <div className="flex items-center">
                          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 transition-all duration-100 ease-linear"
                              style={{ width: `${(undoCountdown / 10) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {undoCountdown}s
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setShowUndoNotification(false)}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enhanced Bulk Operations Modal */}
        <AnimatePresence>
          {showBulkOperationsModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowBulkOperationsModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200 dark:border-gray-700"
                onClick={e => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                        {bulkOperationType === 'stock_update' && <ArrowUpDown className="w-6 h-6 text-white" />}
                        {bulkOperationType === 'location_update' && <MapPin className="w-6 h-6 text-white" />}
                        {bulkOperationType === 'description_update' && <FileText className="w-6 h-6 text-white" />}
                        {bulkOperationType === 'product_link_update' && <LinkIcon className="w-6 h-6 text-white" />}
                        {bulkOperationType === 'image_update' && <ImageIcon className="w-6 h-6 text-white" />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {bulkOperationType === 'stock_update' && 'Toplu Stok Güncelleme'}
                          {bulkOperationType === 'location_update' && 'Toplu Konum Güncelleme'}
                          {bulkOperationType === 'description_update' && 'Toplu Açıklama Güncelleme'}
                          {bulkOperationType === 'product_link_update' && 'Toplu Satın Alma Linki Güncelleme'}
                          {bulkOperationType === 'image_update' && 'Toplu Görsel Güncelleme'}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex items-center space-x-1">
                            <Package className="w-4 h-4 text-white/80" />
                            <span className="text-sm font-medium text-white/90">
                              {selectedProducts.length} ürün seçili
                            </span>
                          </div>
                          <div className="w-1 h-1 bg-white/60 rounded-full"></div>
                          <span className="text-sm text-white/80">
                            {bulkOperationType === 'stock_update' && 'Stok miktarlarını toplu olarak güncelleyin'}
                            {bulkOperationType === 'location_update' && 'Ürün konumlarını toplu olarak değiştirin'}
                            {bulkOperationType === 'description_update' && 'Ürün açıklamalarını toplu olarak güncelleyin'}
                            {bulkOperationType === 'product_link_update' && 'Satın alma linklerini toplu olarak ekleyin'}
                            {bulkOperationType === 'image_update' && 'Ürün görsellerini toplu olarak güncelleyin'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowBulkOperationsModal(false)}
                      className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200 backdrop-blur-sm border border-white/30"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  
                  {/* Operation Status Bar */}
                  <div className="mt-4 p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-white/90 font-medium">İşlem Hazır</span>
                      </div>
                      <div className="flex items-center space-x-4 text-white/80">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>~{Math.ceil(selectedProducts.length / 10)} saniye</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Shield className="w-4 h-4" />
                          <span>Güvenli İşlem</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 space-y-6">
                  {/* Stock Update Section */}
                  {bulkOperationType === 'stock_update' && (
                    <div className="space-y-4">
                      {/* Operation Type Selection */}
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <ArrowUpDown className="w-5 h-5 text-blue-600" />
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Stok İşlem Türü
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Seçili ürünlerin stok miktarları nasıl güncellensin?
                        </p>
                        
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { value: 'add', label: 'Stok Ekle', icon: Plus, color: 'green', desc: 'Mevcut stoka ekle' },
                            { value: 'remove', label: 'Stok Çıkar', icon: Minus, color: 'orange', desc: 'Mevcut stoktan çıkar' },
                            { value: 'set', label: 'Stok Ayarla', icon: Edit3, color: 'blue', desc: 'Yeni değer belirle' }
                          ].map((operation) => {
                            const IconComponent = operation.icon;
                            return (
                              <motion.button
                                key={operation.value}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setBulkOperationData(prev => ({ ...prev, stockOperation: operation.value }))}
                                className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                  bulkOperationData.stockOperation === operation.value
                                    ? `border-${operation.color}-500 bg-${operation.color}-50 dark:bg-${operation.color}-900/20`
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                              >
                                <div className="flex flex-col items-center space-y-2">
                                  <div className={`p-2 rounded-full ${
                                    bulkOperationData.stockOperation === operation.value
                                      ? `bg-${operation.color}-100 dark:bg-${operation.color}-800`
                                      : 'bg-gray-100 dark:bg-gray-800'
                                  }`}>
                                    <IconComponent className={`w-4 h-4 ${
                                      bulkOperationData.stockOperation === operation.value
                                        ? `text-${operation.color}-600 dark:text-${operation.color}-400`
                                        : 'text-gray-500'
                                    }`} />
                                  </div>
                                  <div className="text-center">
                                    <div className={`text-sm font-medium ${
                                      bulkOperationData.stockOperation === operation.value
                                        ? `text-${operation.color}-700 dark:text-${operation.color}-300`
                                        : 'text-gray-700 dark:text-gray-300'
                                    }`}>
                                      {operation.label}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {operation.desc}
                                    </div>
                                  </div>
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Stock Amount Input */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Stok Miktarı
                          </label>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Negatif değerler kabul edilmez</span>
                          </div>
                        </div>
                        
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            value={bulkOperationData.stockAmount}
                            onChange={(e) => setBulkOperationData(prev => ({ ...prev, stockAmount: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-lg font-medium"
                            placeholder="Örn: 50"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 mr-8">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                        
                        {/* Preview */}
                        {bulkOperationData.stockAmount && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center space-x-2">
                              <Info className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                                Önizleme: {selectedProducts.length} ürünün stoğu 
                                {bulkOperationData.stockOperation === 'add' && ` ${bulkOperationData.stockAmount} adet artırılacak`}
                                {bulkOperationData.stockOperation === 'remove' && ` ${bulkOperationData.stockAmount} adet azaltılacak`}
                                {bulkOperationData.stockOperation === 'set' && ` ${bulkOperationData.stockAmount} adet olarak ayarlanacak`}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Location Update Section */}
                  {bulkOperationType === 'location_update' && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-5 h-5 text-green-600" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Yeni Konum Bilgisi
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Seçili {selectedProducts.length} ürünün dolap konumu aşağıdaki değerle güncellenecek.
                      </p>
                      
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Dolap Konumu *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={bulkOperationData.newLocation}
                            onChange={handleBulkLocationChange}
                            className="w-full px-4 py-3 pr-16 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            placeholder="Örn: A-12_1"
                            maxLength={6}
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {bulkOperationData.newLocation ? (
                                <span className={`${bulkOperationData.newLocation.length === 6 ? 'text-green-500 dark:text-green-400' : ''}`}>
                                  {bulkOperationData.newLocation.length}/6
                                </span>
                              ) : 'A-00_1'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Format: <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">Harf-Rakam(2)_Rakam(1-3)</span>
                        </p>
                        
                        {/* Format Examples and Validation */}
                        <div className="space-y-2">
                          {/* Format Examples */}
                          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="flex items-start space-x-2">
                              <Info className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                                  Doğru Format Örnekleri:
                                </p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {['A-12_1', 'B-05_2', 'C-99_3', 'D-01_1'].map((example) => (
                                    <button
                                      key={example}
                                      onClick={() => setBulkOperationData(prev => ({ ...prev, newLocation: example }))}
                                      className="px-2 py-1 text-xs font-mono bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-700 transition-colors"
                                    >
                                      {example}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Validation Status */}
                          {bulkOperationData.newLocation && (
                            <div className={`p-3 rounded-lg border ${
                              bulkOperationData.newLocation.length === 6 
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                            }`}>
                              <div className="flex items-center space-x-2">
                                {bulkOperationData.newLocation.length === 6 ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <span className="text-sm text-green-700 dark:text-green-300 font-medium">
                                      Geçerli format: {bulkOperationData.newLocation}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                    <span className="text-sm text-yellow-700 dark:text-yellow-300">
                                      Format tamamlanmadı ({bulkOperationData.newLocation.length}/6 karakter)
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Description Update Section */}
                  {bulkOperationType === 'description_update' && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-5 h-5 text-purple-600" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Yeni Açıklama
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Seçili {selectedProducts.length} ürünün açıklaması güncellenecek.
                      </p>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Açıklama Metni
                          </label>
                          <span className="text-xs text-gray-500">
                            {bulkOperationData.newDescription?.length || 0}/500 karakter
                          </span>
                        </div>
                        
                        <textarea
                          value={bulkOperationData.newDescription}
                          onChange={(e) => setBulkOperationData(prev => ({ ...prev, newDescription: e.target.value }))}
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                          placeholder="Ürün açıklamasını buraya yazın..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Product Link Update Section */}
                  {bulkOperationType === 'product_link_update' && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <LinkIcon className="w-5 h-5 text-cyan-600" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Satın Alma Linki
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Seçili {selectedProducts.length} ürün için satın alma linki eklenecek.
                      </p>
                      
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Ürün Linki (URL)
                        </label>
                        <div className="relative">
                          <input
                            type="url"
                            value={bulkOperationData.newProductLink}
                            onChange={(e) => setBulkOperationData(prev => ({ ...prev, newProductLink: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            placeholder="https://example.com/product-link"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <LinkIcon className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                        
                        {/* Link Preview */}
                        {bulkOperationData.newProductLink && (
                          <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                            <div className="flex items-center space-x-2">
                              <Eye className="w-4 h-4 text-cyan-600" />
                              <span className="text-sm text-cyan-700 dark:text-cyan-300 font-medium">
                                Önizleme:
                              </span>
                            </div>
                            <a 
                              href={bulkOperationData.newProductLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-cyan-600 hover:text-cyan-800 underline break-all"
                            >
                              {bulkOperationData.newProductLink}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Image Update Section */}
                  {bulkOperationType === 'image_update' && (
                    <div className="space-y-6">
                      <div className="flex items-center space-x-2">
                        <ImageIcon className="w-6 h-6 text-pink-600" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Ürün Görseli Güncelleme
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Seçili {selectedProducts.length} ürün için görsel güncellenecek.
                      </p>
                      
                      {/* Upload Method Selection */}
                      <div className="space-y-4">
                        <div className="flex justify-center space-x-6 mb-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              checked={bulkOperationData.imageUploadMethod === 'file'}
                              onChange={() => setBulkOperationData(prev => ({ 
                                ...prev, 
                                imageUploadMethod: 'file',
                                newImageUrl: '',
                                selectedImageFile: null
                              }))}
                              className="w-4 h-4 text-pink-600 border-gray-300 focus:ring-pink-500"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Dosya Yükle
                            </span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              checked={bulkOperationData.imageUploadMethod === 'url'}
                              onChange={() => setBulkOperationData(prev => ({ 
                                ...prev, 
                                imageUploadMethod: 'url',
                                newImageUrl: '',
                                selectedImageFile: null
                              }))}
                              className="w-4 h-4 text-pink-600 border-gray-300 focus:ring-pink-500"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              URL ile Ekle
                            </span>
                          </label>
                        </div>
                        
                        {/* File Upload */}
                        {bulkOperationData.imageUploadMethod === 'file' && (
                          <div className="flex flex-col items-center space-y-4">
                            <div 
                              className={`w-80 h-80 border-2 border-dashed ${
                                dragActive 
                                  ? 'border-pink-500 dark:border-pink-600 bg-pink-50 dark:bg-pink-900/20' 
                                  : 'border-gray-300 dark:border-gray-600'
                              } rounded-lg flex flex-col items-center justify-center p-6 hover:border-pink-500 dark:hover:border-pink-600 transition-colors cursor-pointer group`}
                              onDragEnter={handleBulkImageDrag}
                              onDragLeave={handleBulkImageDrag}
                              onDragOver={handleBulkImageDrag}
                              onDrop={handleBulkImageDrop}
                              onClick={handleBulkDropzoneClick}
                            >
                              {bulkOperationData.selectedImageFile ? (
                                <div className="w-full h-full flex flex-col items-center justify-center">
                                  <img 
                                    src={URL.createObjectURL(bulkOperationData.selectedImageFile)} 
                                    alt="Ürün önizleme" 
                                    className="max-h-60 max-w-full object-contain rounded-lg shadow-sm" 
                                  />
                                  <div className="mt-3 text-center">
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {truncateFileName(bulkOperationData.selectedImageFile.name)}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {formatFileSize(bulkOperationData.selectedImageFile.size)}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Upload className="w-16 h-16 text-gray-400 mb-4 group-hover:text-pink-500 transition-colors" />
                                  <p className="text-lg font-medium text-gray-700 dark:text-gray-300 text-center mb-2">
                                    Görsel yüklemek için tıklayın veya sürükleyin
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                    PNG, JPG, GIF, WebP formatları desteklenir
                                  </p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                    Maksimum dosya boyutu: 50MB
                                  </p>
                                </>
                              )}
                              <input 
                                ref={bulkImageInputRef}
                                type="file" 
                                onChange={handleBulkImageFileChange} 
                                className="hidden" 
                                accept="image/*" 
                              />
                            </div>
                            
                            {/* File Info */}
                            {bulkOperationData.selectedImageFile && (
                              <div className="w-80 p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <CheckCircle className="w-5 h-5 text-pink-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-pink-700 dark:text-pink-300 truncate">
                                      {truncateFileName(bulkOperationData.selectedImageFile.name)}
                                    </p>
                                    <div className="flex items-center space-x-4 mt-1">
                                      <span className="text-xs text-pink-600 dark:text-pink-400">
                                        {formatFileSize(bulkOperationData.selectedImageFile.size)}
                                      </span>
                                      <span className="text-xs text-pink-600 dark:text-pink-400">
                                        {bulkOperationData.selectedImageFile.type}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setBulkOperationData(prev => ({ ...prev, selectedImageFile: null }));
                                    }}
                                    className="flex-shrink-0 p-1 text-pink-600 hover:text-pink-800 transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* URL Input */}
                        {bulkOperationData.imageUploadMethod === 'url' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Görsel URL'si
                              </label>
                              <div className="relative">
                                <input
                                  type="url"
                                  value={bulkOperationData.newImageUrl}
                                  onChange={handleBulkImageUrlChange}
                                  className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                  placeholder="https://example.com/image.jpg"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <LinkIcon className="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                            </div>
                            
                            {/* Image Preview */}
                            {bulkOperationData.newImageUrl && (
                              <div className="flex justify-center">
                                <div className="w-80 h-80 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center p-4 overflow-hidden bg-gray-50 dark:bg-gray-800">
                                  <img 
                                    src={bulkOperationData.newImageUrl}
                                    alt="Ürün önizleme" 
                                    className="max-h-full max-w-full object-contain rounded-lg"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='%23ddd'/%3e%3ctext x='50%25' y='50%25' font-size='18' text-anchor='middle' alignment-baseline='middle'%3eGörsel Yüklenemedi%3c/text%3e%3c/svg%3e";
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {/* URL Validation */}
                            {bulkOperationData.newImageUrl && (
                              <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800">
                                <div className="flex items-start space-x-2">
                                  <Eye className="w-4 h-4 text-pink-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-pink-700 dark:text-pink-300">
                                      URL Önizleme
                                    </p>
                                    <p className="text-xs text-pink-600 dark:text-pink-400 break-all">
                                      {bulkOperationData.newImageUrl}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    {/* Operation Summary */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                        <Package className="w-4 h-4" />
                        <span>{selectedProducts.length} ürün seçili</span>
                      </div>
                      
                      {/* Validation Status */}
                      <div className="flex items-center space-x-2">
                        {(
                          (bulkOperationType === 'stock_update' && bulkOperationData.stockAmount) ||
                          (bulkOperationType === 'location_update' && bulkOperationData.newLocation?.length === 6) ||
                          (bulkOperationType === 'description_update' && bulkOperationData.newDescription?.trim()) ||
                          (bulkOperationType === 'product_link_update' && bulkOperationData.newProductLink?.trim()) ||
                          (bulkOperationType === 'image_update' && (bulkOperationData.newImageUrl?.trim() || bulkOperationData.selectedImageFile))
                        ) ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                              Hazır
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            <span className="text-sm text-amber-600 dark:text-amber-400">
                              Eksik bilgi
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowBulkOperationsModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors duration-200"
                      >
                        İptal
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setShowBulkOperationsModal(false);
                          executeBulkOperation();
                        }}
                        disabled={
                          (bulkOperationType === 'stock_update' && !bulkOperationData.stockAmount) ||
                          (bulkOperationType === 'location_update' && bulkOperationData.newLocation?.length !== 6) ||
                          (bulkOperationType === 'description_update' && !bulkOperationData.newDescription?.trim()) ||
                          (bulkOperationType === 'product_link_update' && !bulkOperationData.newProductLink?.trim()) ||
                          (bulkOperationType === 'image_update' && !bulkOperationData.newImageUrl?.trim() && !bulkOperationData.selectedImageFile)
                        }
                        className={`flex items-center space-x-2 px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          (
                            (bulkOperationType === 'stock_update' && bulkOperationData.stockAmount) ||
                            (bulkOperationType === 'location_update' && bulkOperationData.newLocation?.length === 6) ||
                            (bulkOperationType === 'description_update' && bulkOperationData.newDescription?.trim()) ||
                            (bulkOperationType === 'product_link_update' && bulkOperationData.newProductLink?.trim()) ||
                            (bulkOperationType === 'image_update' && (bulkOperationData.newImageUrl?.trim() || bulkOperationData.selectedImageFile))
                          )
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-purple-700'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <ArrowUpDown className="w-4 h-4" />
                        <span>İşlemi Başlat</span>
                        {selectedProducts.length > 0 && (
                          <div className="bg-white/20 px-2 py-1 rounded-full text-xs font-bold">
                            {selectedProducts.length}
                          </div>
                        )}
                      </motion.button>
                    </div>
                  </div>
                  
                  {/* Quick Tips */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start space-x-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="font-medium">İpucu: </span>
                        {bulkOperationType === 'stock_update' && 'Stok işlemleri geri alınamaz. İşlem öncesi mevcut stok miktarlarını kontrol edin.'}
                        {bulkOperationType === 'location_update' && 'Konum bilgisi tüm seçili ürünler için aynı olacak. Farklı konumlar için işlemi ayrı ayrı yapın.'}
                        {bulkOperationType === 'description_update' && 'Açıklama tüm seçili ürünlerde aynı olacak. Mevcut açıklamalar tamamen değiştirilecek.'}
                        {bulkOperationType === 'product_link_update' && 'Satın alma linki tüm seçili ürünler için aynı olacak. Geçerli bir URL formatı kullanın.'}
                        {bulkOperationType === 'image_update' && 'Görsel tüm seçili ürünler için aynı olacak. Yüksek kaliteli görseller kullanın.'}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Import Modal */}
        <AnimatePresence>
          {showImportModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
              onClick={() => setShowImportModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-2 bg-teal-100 dark:bg-teal-900/20 rounded-full">
                      <Upload className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Ürün İçe Aktarma
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        CSV veya JSON dosyasından ürün verileri içe aktarın
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Dosya Seçin
                      </label>
                      <input
                        type="file"
                        accept=".csv,.json"
                        onChange={handleFileUpload}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Desteklenen formatlar: CSV, JSON (Maksimum 10MB)
                      </p>
                    </div>
                    
                    {importPreview.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Dosya Önizlemesi (İlk 10 kayıt)
                        </h4>
                        <div className="max-h-60 overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                              <tr>
                                {Object.keys(importPreview[0] || {}).map(key => (
                                  <th key={key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {key}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {importPreview.map((row, index) => (
                                <tr key={index}>
                                  {Object.values(row).map((value, idx) => (
                                    <td key={idx} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                      {String(value).substring(0, 50)}...
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowImportModal(false);
                        setImportFile(null);
                        setImportPreview([]);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={() => {
                        setShowImportModal(false);
                        executeImport();
                      }}
                      disabled={!importFile || importPreview.length === 0}
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      <span>İçe Aktar</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bulk Progress Modal */}
        <AnimatePresence>
          {showBulkProgress && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                      <svg className="animate-spin w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Toplu İşlem Devam Ediyor...
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {bulkProcessedCount}/{bulkTotalCount} ürün işlendi
                      </p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-blue-600 dark:bg-blue-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${bulkOperationProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 text-center">
                      %{bulkOperationProgress} tamamlandı
                    </p>
                  </div>

                  {bulkOperationResults.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        İşlem Sonuçları
                      </h4>
                      <div className="max-h-60 overflow-auto space-y-2">
                        {bulkOperationResults.map((result, index) => (
                          <div
                            key={index}
                            className={`p-2 rounded-lg text-sm ${
                              result.status === 'success'
                                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                            }`}
                          >
                            <div className="font-medium">
                              {result.productName || result.productId}
                            </div>
                            <div className="text-xs opacity-75">
                              {result.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkOperationProgress === 100 && (
                    <div className="flex justify-end mt-6">
                      <button
                        onClick={() => {
                          setShowBulkProgress(false);
                          setBulkOperationProgress(0);
                          setBulkProcessedCount(0);
                          setBulkOperationResults([]);
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        Kapat
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default StokYonetimi; 